import { orderNotionalFromLots, resolveLotSize, withOrderLots } from "../../../../lib/orderLots";
import { applyAutoTwapProfile } from "../../../../lib/liquidityAutoTwap";
import { buildTradingPnlBreakdown } from "../../../../lib/tradingPnl";
import { isWithinTradingSession } from "../../../../lib/tradingSession";
import { buildFallbackExchangeSchedule, mockInstrumentTradability } from "../../../../lib/exchangeSchedule";
const now = () => new Date().toISOString();
const daysAgoIso = (days) => new Date(Date.now() - days * 86400000).toISOString();
/** TTL секрета брокерского API в моке (90 суток). */
const BROKER_TOKEN_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const brokerTokenExpiresAt = (issuedAtIso) => new Date(new Date(issuedAtIso).getTime() + BROKER_TOKEN_TTL_MS).toISOString();
/** Канонические пользователи мока; `me()` подмешивает актуальные поля сюда по `id` из localStorage. */
const USERS = [
    {
        id: "u1",
        email: "user@example.com",
        name: "Trader",
        activation_status: "active",
        roles: ["trader"],
        registered_at: daysAgoIso(30),
        sandbox_enabled: true,
    },
    {
        id: "u-admin",
        email: "admin@example.com",
        name: "Admin",
        activation_status: "active",
        roles: ["admin", "trader"],
        registered_at: daysAgoIso(60),
        sandbox_enabled: true,
    },
    {
        id: "u-pend",
        email: "pending@example.com",
        name: "Pending",
        activation_status: "pending_activation",
        roles: ["trader"],
        registered_at: daysAgoIso(2),
        sandbox_enabled: true,
    },
];
const TOKENS = (() => {
    const issuedSandbox = daysAgoIso(20);
    const issuedLive = daysAgoIso(75);
    const expSandbox = brokerTokenExpiresAt(issuedSandbox);
    const expLive = brokerTokenExpiresAt(issuedLive);
    return [
        {
            id: "t1",
            token: "sandbox-demo-token",
            mode: "sandbox",
            is_valid: true,
            updated_at: now(),
            issued_at: issuedSandbox,
            expires_at: expSandbox,
            rotate_until_at: expSandbox,
            last_validated_at: daysAgoIso(2),
            last_validation_outcome: "ok",
            rotation_zero_downtime: true,
        },
        {
            id: "t2",
            token: "live-demo-token",
            mode: "live",
            is_valid: false,
            updated_at: now(),
            issued_at: issuedLive,
            expires_at: expLive,
            rotate_until_at: expLive,
            last_validated_at: daysAgoIso(5),
            last_validation_outcome: "failed",
            rotation_zero_downtime: true,
        },
    ];
})();
const STRATEGIES = [
    {
        id: "s1",
        mode: "sandbox",
        name: "MA Cross",
        is_active: true,
        params: { fast_ma: 10, slow_ma: 30, stop_loss: 2.5, take_profit: 4.0, lot: 1 },
    },
];
const STRATEGY_VERSIONS = [
    {
        id: "sv1",
        strategy_id: "s1",
        mode: "sandbox",
        created_at: now(),
        author: "Trader",
        params: { fast_ma: 10, slow_ma: 30, stop_loss: 2.5, take_profit: 4.0, lot: 1 },
    },
];
const STRATEGY_PRESETS = [
    {
        id: "conservative",
        name: "Conservative",
        params: { fast_ma: 14, slow_ma: 50, stop_loss: 1.5, take_profit: 2.5, lot: 1, block_ma_sell_in_loss: 1, lock_stop_loss_on_buy: 1 },
    },
    {
        id: "balanced",
        name: "Balanced",
        params: { fast_ma: 10, slow_ma: 30, stop_loss: 2.5, take_profit: 4.0, lot: 1, block_ma_sell_in_loss: 1, lock_stop_loss_on_buy: 1 },
    },
    {
        id: "aggressive",
        name: "Aggressive",
        params: { fast_ma: 7, slow_ma: 21, stop_loss: 3.5, take_profit: 6.0, lot: 2, block_ma_sell_in_loss: 1, lock_stop_loss_on_buy: 1 },
    },
];
const AB_EXPERIMENTS = [
    {
        id: "ab-exp-1",
        mode: "sandbox",
        name: "MA Cross AB #1",
        hypothesis: "Более агрессивные параметры должны снизить лаг входа без роста drawdown.",
        strategy_a_id: "s1",
        strategy_b_id: "s1",
        traffic_split_pct_a: 50,
        period_from: now(),
        period_to: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        stop_criteria: {
            min_trades_per_branch: 30,
            max_drawdown_limit_pct: 0.2,
            min_slippage_improvement_pct: 0.05,
        },
        status: "draft",
        created_at: now(),
        updated_at: now(),
    },
];
const AB_EXPERIMENT_DECISION_LOGS = {};
const WHATIF_EXPERIMENTS = [];
const orderRejectTaxonomy = (code) => {
    if (!code)
        return undefined;
    if (code.startsWith("PORTFOLIO_LIMIT_") || code.startsWith("PORTFOLIO_"))
        return "portfolio";
    if (code.startsWith("RISK_"))
        return "risk";
    if (code.startsWith("PRETRADE_MARKET_DATA") || code.includes("MARKET_DATA"))
        return "market_data";
    if (code.startsWith("VENUE_") || code.includes("ROUTER"))
        return "venue";
    if (code === "VALIDATION_ERROR" || code.startsWith("VALIDATION"))
        return "validation";
    if (code.startsWith("RUNTIME_") || code.startsWith("KILL_SWITCH"))
        return "runtime";
    if (code.startsWith("IDEMPOTENCY"))
        return "idempotency";
    return "other";
};
const queuePositionProxyForOrder = (order) => {
    let h = 0;
    const s = `${order.id}:${order.ticker}`;
    for (let i = 0; i < s.length; i++)
        h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return 5 + (h % 46);
};
const ORDERS = [
    {
        id: "o1",
        mode: "sandbox",
        strategy_id: "s1",
        execution_profile: "balanced",
        execution_venue: "moex",
        ticker: "SBER",
        side: "BUY",
        type: "MARKET",
        qty: 1,
        price: 311.2,
        status: "filled",
        expected_price: 311.2,
        realized_price: 311.35,
        slippage_bps: 4.8,
        latency_ms: 180,
        fee: 0.22,
        fee_source: "estimated",
        tax: 0.06,
        partial_fill_ratio: 1,
        created_at: now(),
        time_to_fill_ms: 180,
        signal_source: "ma_cross",
    },
    {
        id: "o2",
        mode: "live",
        strategy_id: "s1",
        execution_profile: "balanced",
        execution_venue: "moex",
        ticker: "GAZP",
        side: "SELL",
        type: "LIMIT",
        qty: 2,
        price: 168.5,
        status: "open",
        expected_price: 168.5,
        created_at: now(),
        queue_position_proxy: queuePositionProxyForOrder({ id: "o2", ticker: "GAZP" }),
    },
    {
        id: "o3",
        mode: "sandbox",
        strategy_id: "s1",
        execution_profile: "balanced",
        execution_venue: "moex",
        ticker: "LKOH",
        side: "BUY",
        type: "LIMIT",
        qty: 1,
        price: 7420.4,
        status: "open",
        expected_price: 7420.4,
        created_at: now(),
        queue_position_proxy: queuePositionProxyForOrder({ id: "o3", ticker: "LKOH" }),
    },
    {
        id: "o4",
        mode: "live",
        strategy_id: "s1",
        execution_profile: "balanced",
        execution_venue: "spb",
        ticker: "SBER",
        side: "BUY",
        type: "LIMIT",
        qty: 3,
        price: 312.3,
        status: "open",
        expected_price: 312.3,
        created_at: now(),
        queue_position_proxy: queuePositionProxyForOrder({ id: "o4", ticker: "SBER" }),
    },
    {
        id: "o-rej-1",
        mode: "sandbox",
        strategy_id: "s1",
        execution_profile: "balanced",
        execution_venue: "moex",
        ticker: "SBER",
        side: "BUY",
        type: "LIMIT",
        qty: 1,
        price: 300,
        status: "rejected",
        reject_reason_code: "RISK_MAX_NOTIONAL_LIMIT",
        reject_reason_taxonomy: "risk",
        expected_price: 300,
        created_at: now(),
    },
];
const SHADOW_ORDERS = [];
const POSITIONS = [
    {
        id: "p1",
        mode: "sandbox",
        strategy_id: "s1",
        ticker: "SBER",
        qty: 5,
        avg_price: 308.2,
        market_price: 311.7,
        pnl: 17.5,
        currency: "RUB",
    },
    {
        id: "p2",
        mode: "live",
        strategy_id: "s1",
        ticker: "GAZP",
        qty: -2,
        avg_price: 170.1,
        market_price: 168.4,
        pnl: 3.4,
        currency: "RUB",
    },
];
const RISK_LIMITS = {
    sandbox: {
        mode: "sandbox",
        max_daily_loss: 10000,
        max_weekly_loss: 35000,
        max_trades_per_day: 25,
        max_notional_per_day: 500000,
        kill_switch_enabled: false,
        updated_at: now(),
    },
    live: {
        mode: "live",
        max_daily_loss: 5000,
        max_weekly_loss: 15000,
        max_trades_per_day: 12,
        max_notional_per_day: 250000,
        kill_switch_enabled: false,
        updated_at: now(),
    },
};
const defaultStrategyPolicyGuards = () => ({
    expected_slippage_bps: 12,
    max_spread_bps: 45,
    min_liquidity_notional: 350000,
    trading_schedule: {
        session_start: "10:00",
        session_end: "18:45",
        timezone: "Europe/Moscow",
        only_rth: true,
    },
    news_blackout_minutes: 20,
});
const normalizeStrategyTickers = (tickers, allowed) => {
    if (tickers && tickers.length > 0) {
        const seen = new Set();
        return tickers
            .map((item) => ({
            ticker: String(item.ticker || "")
                .trim()
                .toUpperCase(),
            lock_stop_loss: item.lock_stop_loss !== false,
            lock_buy: Boolean(item.lock_buy),
        }))
            .filter((item) => {
            if (!item.ticker || seen.has(item.ticker))
                return false;
            seen.add(item.ticker);
            return true;
        });
    }
    return (allowed ?? []).map((ticker) => ({
        ticker: ticker.toUpperCase(),
        lock_stop_loss: true,
        lock_buy: false,
    }));
};
const mergeStrategyPolicyRow = (p) => {
    const d = defaultStrategyPolicyGuards();
    const tickers = normalizeStrategyTickers(p.tickers, p.allowed_tickers);
    return {
        strategy_id: p.strategy_id,
        mode: p.mode,
        priority: p.priority ?? 50,
        conflict_policy: p.conflict_policy ?? "skip",
        tickers,
        allowed_tickers: tickers.map((item) => item.ticker),
        execution_profile: p.execution_profile ?? "balanced",
        expected_slippage_bps: p.expected_slippage_bps ?? d.expected_slippage_bps,
        max_spread_bps: p.max_spread_bps ?? d.max_spread_bps,
        min_liquidity_notional: p.min_liquidity_notional ?? d.min_liquidity_notional,
        trading_schedule: {
            ...d.trading_schedule,
            ...(p.trading_schedule ?? {}),
        },
        news_blackout_minutes: p.news_blackout_minutes ?? d.news_blackout_minutes,
        updated_at: p.updated_at ?? now(),
    };
};
const TICKERS_BY_MODE = {
    sandbox: ["SBER", "GAZP", "LKOH", "YNDX"],
    live: ["SBER", "TATN", "ROSN", "NVTK"],
};
const resolveMarketWatchlist = (mode) => {
    const active = STRATEGIES.filter((strategy) => strategy.mode === mode && strategy.is_active);
    const merged = new Set();
    for (const strategy of active) {
        const policy = STRATEGY_POLICIES[strategy.id];
        const allowed = policy?.allowed_tickers ?? [];
        if (allowed.length > 0) {
            allowed.forEach((ticker) => merged.add(ticker.toUpperCase()));
        }
        else {
            TICKERS_BY_MODE[mode].forEach((ticker) => merged.add(ticker));
        }
    }
    if (merged.size === 0) {
        return [...TICKERS_BY_MODE[mode]];
    }
    return Array.from(merged).sort();
};
const STRATEGY_POLICIES = {
    s1: mergeStrategyPolicyRow({
        strategy_id: "s1",
        mode: "sandbox",
        priority: 50,
        conflict_policy: "reduce",
        allowed_tickers: ["SBER", "GAZP", "LKOH"],
        execution_profile: "balanced",
        updated_at: now(),
    }),
};
const RISK_POLICIES = {
    sandbox: {
        mode: "sandbox",
        max_daily_loss: 10000,
        max_weekly_loss: 35000,
        max_notional_per_day: 500000,
        max_trades_per_day: 25,
        kill_switch_enabled: false,
        max_position_notional: 120000,
        max_simultaneous_positions: 5,
        allowed_tickers: [],
        blocked_tickers: [],
        max_exposure_per_ticker: 90000,
        max_orders_per_ticker_per_hour: 8,
        slippage_threshold_bps: 25,
        volatility_guard_enabled: true,
        commission_rate_pct: 0.3,
        updated_at: now(),
    },
    live: {
        mode: "live",
        max_daily_loss: 5000,
        max_weekly_loss: 15000,
        max_notional_per_day: 250000,
        max_trades_per_day: 12,
        kill_switch_enabled: false,
        max_position_notional: 70000,
        max_simultaneous_positions: 3,
        allowed_tickers: ["SBER", "TATN", "ROSN"],
        blocked_tickers: ["NVTK"],
        max_exposure_per_ticker: 50000,
        max_orders_per_ticker_per_hour: 5,
        slippage_threshold_bps: 18,
        volatility_guard_enabled: true,
        commission_rate_pct: 0.3,
        updated_at: now(),
    },
};
let USER_TRADING_PREFS = {
    notifications: {
        email_on_kill_switch: false,
        email_on_api_down: true,
        email_on_critical_reason: true,
        updated_at: now(),
    },
    schedule: {
        session_start: "10:00",
        session_end: "18:50",
        timezone: "Europe/Moscow",
        only_rth: true,
    },
    lot_settings: {
        min: 1,
        max: 100,
        step: 1,
        updated_at: now(),
    },
    updated_at: now(),
};
/** Временная блокировка stop loss по режиму. */
const SELL_LOCKED_TICKERS = {
    sandbox: new Set(),
    live: new Set(),
};
/** Полная блокировка любых продаж по режиму. */
const ALL_SELLS_BLOCKED_TICKERS = {
    sandbox: new Set(),
    live: new Set(),
};
const TRACE_ENTRIES = (() => {
    const t0 = new Date(Date.now() - 3600000).toISOString();
    const t1 = new Date(Date.now() - 3500000).toISOString();
    const t2 = new Date(Date.now() - 3400000).toISOString();
    const t3 = new Date(Date.now() - 3300000).toISOString();
    const t4 = new Date(Date.now() - 3200000).toISOString();
    return [
        {
            id: "tr-1",
            mode: "sandbox",
            correlation_id: "corr-s1",
            strategy_id: "s1",
            ticker: "SBER",
            started_at: t0,
            steps: [
                { stage: "signal", status: "ok", at: t0, detail: "BUY signal, conf=0.72" },
                { stage: "risk_gate", status: "ok", at: t1, detail: "Within daily loss & exposure" },
                { stage: "router", status: "ok", at: t2, detail: "LIMIT @ 311.20 → primary moex" },
                { stage: "venue", status: "ok", at: t3, detail: "MOEX ACK, filled 1 lot, slippage 3 bps" },
            ],
        },
        {
            id: "tr-2",
            mode: "live",
            correlation_id: "corr-l1",
            strategy_id: "s1",
            ticker: "GAZP",
            started_at: t0,
            steps: [
                { stage: "signal", status: "ok", at: t0, detail: "SELL signal" },
                { stage: "risk_gate", status: "rejected", at: t1, detail: "Blocked by policy", reason_code: "RISK_MAX_NOTIONAL_LIMIT" },
                { stage: "router", status: "pending", at: t1, detail: "—" },
                { stage: "venue", status: "pending", at: t1, detail: "—" },
            ],
        },
        {
            id: "tr-3",
            mode: "sandbox",
            correlation_id: "corr-s2",
            strategy_id: "s1",
            ticker: "LKOH",
            started_at: t1,
            steps: [
                { stage: "signal", status: "ok", at: t0, detail: "BUY" },
                { stage: "risk_gate", status: "ok", at: t1, detail: "OK" },
                { stage: "router", status: "ok", at: t2, detail: "MARKET, venue order id mock-8821" },
                { stage: "venue", status: "ok", at: t3, detail: "Partial fill 0.5 lot @ primary" },
            ],
        },
        {
            id: "tr-4",
            mode: "sandbox",
            correlation_id: "corr-s-venue",
            strategy_id: "s1",
            ticker: "TATN",
            started_at: t2,
            steps: [
                { stage: "signal", status: "ok", at: t0, detail: "BUY" },
                { stage: "risk_gate", status: "ok", at: t1, detail: "Pre-trade checks passed" },
                { stage: "router", status: "ok", at: t2, detail: "Routed primary moex, slice 1/1" },
                { stage: "venue", status: "rejected", at: t4, detail: "Gateway timeout submitting to venue", reason_code: "VENUE_GATEWAY_TIMEOUT" },
            ],
        },
    ];
})();
const SIGNAL_SKIP_ENTRIES = (() => {
    const t0 = new Date(Date.now() - 1800000).toISOString();
    const t1 = new Date(Date.now() - 600000).toISOString();
    return [
        {
            id: "ssl-mock-1",
            mode: "sandbox",
            strategy_id: "s1",
            ticker: "SBER",
            side: "BUY",
            reason_code: "SILENT_OPEN_ORDER",
            message: "Уже есть открытая заявка BUY — новый сигнал не отправляется.",
            source: "auto_tick",
            details: { open_order_id: "o-mock-open-1" },
            first_at: t0,
            last_at: t1,
            hit_count: 12,
        },
        {
            id: "ssl-mock-2",
            mode: "sandbox",
            strategy_id: "s1",
            ticker: "GAZP",
            side: "BUY",
            reason_code: "PRETRADE_MARGIN_NOT_ENABLED",
            message: "На счёте недостаточно собственных средств, а маржинальная торговля недоступна.",
            source: "order_create",
            details: {},
            first_at: t1,
            last_at: t1,
            hit_count: 1,
        },
        {
            id: "ssl-mock-3",
            mode: "sandbox",
            strategy_id: "s1",
            ticker: "RUAL",
            side: "BUY",
            reason_code: "SILENT_INSTRUMENT_NOT_TRADABLE",
            message: "Инструмент недоступен: Аукцион закрытия.",
            source: "auto_tick",
            details: {
                schedule_source: "tinvest",
                schedule_timezone: "Europe/Moscow",
                schedule_exchange: "MOEX",
                main_window: "10:00–18:54",
                evening_window: "19:05–23:50",
                is_trading_day: true,
                schedule_date: "2026-08-19",
                tradability_source: "tinvest",
                trading_status: "SECURITY_TRADING_STATUS_CLOSING_AUCTION",
                blocked_reasons: ["Вне расписания биржи: сейчас 18:55, окна 10:00–18:54 (Europe/Moscow)."],
                broker_message: "Инструмент недоступен: Аукцион закрытия.",
            },
            first_at: t1,
            last_at: t1,
            hit_count: 3,
        },
    ];
})();
const EXECUTION_POLICIES = {
    sandbox: {
        mode: "sandbox",
        routing_profile: "smart",
        limit_offset_bps: 8,
        adaptive_offset_enabled: true,
        adaptive_offset_max_extra_bps: 12,
        max_child_order_slices: 4,
        min_child_order_qty_ratio: 0.08,
        liquidity_degraded_fallback: "widen_offset",
        retry_count: 2,
        retry_backoff_ms: 300,
        partial_fill_timeout_sec: 25,
        cancel_replace_enabled: true,
        emergency_exit_mode: "market",
        primary_venue: "moex",
        venue_failover_order: ["spb", "otc"],
        venue_switch_criterion: "min_spread_bps",
        updated_at: now(),
    },
    live: {
        mode: "live",
        routing_profile: "passive",
        limit_offset_bps: 5,
        adaptive_offset_enabled: true,
        adaptive_offset_max_extra_bps: 10,
        max_child_order_slices: 3,
        min_child_order_qty_ratio: 0.1,
        liquidity_degraded_fallback: "widen_and_slow",
        primary_venue: "moex",
        venue_failover_order: ["otc", "spb"],
        venue_switch_criterion: "lowest_latency",
        retry_count: 1,
        retry_backoff_ms: 350,
        partial_fill_timeout_sec: 18,
        cancel_replace_enabled: true,
        emergency_exit_mode: "cancel_only",
        updated_at: now(),
    },
};
const PORTFOLIO_LIMITS = {
    sandbox: {
        mode: "sandbox",
        currency: "RUB",
        total_budget_notional: 600000,
        net_exposure_cap_notional: 280000,
        gross_exposure_cap_notional: 420000,
        max_portfolio_reject_rate: 0.15,
        strategy_budgets: [
            { key: "s1", name: "MA Cross", limit_notional: 220000 },
            { key: "s-sandbox-momentum", name: "Momentum Sandbox", limit_notional: 180000 },
        ],
        cluster_budgets: [
            { key: "oil_gas", name: "Oil & Gas", limit_notional: 200000 },
            { key: "banks", name: "Banks", limit_notional: 160000 },
        ],
        updated_at: now(),
    },
    live: {
        mode: "live",
        currency: "RUB",
        total_budget_notional: 300000,
        net_exposure_cap_notional: 140000,
        gross_exposure_cap_notional: 210000,
        max_portfolio_reject_rate: 0.15,
        strategy_budgets: [{ key: "s1", name: "MA Cross", limit_notional: 120000 }],
        cluster_budgets: [
            { key: "oil_gas", name: "Oil & Gas", limit_notional: 100000 },
            { key: "banks", name: "Banks", limit_notional: 80000 },
        ],
        updated_at: now(),
    },
};
const createEmptyKpiState = () => ({
    order_attempts: 0,
    portfolio_rejections: 0,
    by_code: {},
    live_net_exposure_overage_count: 0,
});
const PORTFOLIO_KPI = {
    sandbox: createEmptyKpiState(),
    live: createEmptyKpiState(),
};
const CLUSTER_BY_TICKER = {
    SBER: "banks",
    TATN: "oil_gas",
    ROSN: "oil_gas",
    GAZP: "oil_gas",
    NVTK: "oil_gas",
};
/** Последняя рыночная цена по режиму/тикеру (без хардкода эталонов). */
const LAST_MARKET_PRICE = {
    sandbox: { SBER: 311.7 },
    live: { GAZP: 168 },
};
const LOSS_STREAK_BY_MODE = { sandbox: 0, live: 0 };
const COOLDOWN_UNTIL_BY_MODE = { sandbox: 0, live: 0 };
const REJECT_STREAK_BY_STRATEGY = {};
const ORDER_IDEMPOTENCY_CACHE = new Map();
const MARKET_DATA_HEALTH = {
    sandbox: { last_primary_at: Date.now(), last_fallback_at: Date.now(), last_source: "primary" },
    live: { last_primary_at: Date.now(), last_fallback_at: Date.now(), last_source: "primary" },
};
const KILL_SWITCH_STATE = {
    account: false,
    mode: { sandbox: false, live: false },
    strategy: {},
};
const RUNTIME_POLICIES = {
    sandbox: {
        mode: "sandbox",
        version: "mock-1",
        effective_from: now(),
        pretrade: {
            price_band_pct: 0.08,
            cooldown_loss_streak: 3,
            cooldown_ms: 5 * 60 * 1000,
            position_transfer_allow_open_orders: false,
        },
        market_data: {
            primary_stale_ms: 12000,
            fallback_stale_ms: 25000,
            block_trading_on_degraded: true,
        },
        posttrade: {
            auto_deactivate_reject_streak: 2,
        },
        kill_switch: {
            scopes: ["strategy", "mode", "account"],
            default_scope: "mode",
        },
        shadow_rollout: {
            enabled: false,
            canary_percent: 0,
            strategy_whitelist: [],
            account_whitelist: [],
        },
    },
    live: {
        mode: "live",
        version: "mock-1",
        effective_from: now(),
        pretrade: {
            price_band_pct: 0.08,
            cooldown_loss_streak: 3,
            cooldown_ms: 5 * 60 * 1000,
            position_transfer_allow_open_orders: false,
        },
        market_data: {
            primary_stale_ms: 12000,
            fallback_stale_ms: 25000,
            block_trading_on_degraded: true,
        },
        posttrade: {
            auto_deactivate_reject_streak: 2,
        },
        kill_switch: {
            scopes: ["strategy", "mode", "account"],
            default_scope: "mode",
        },
        shadow_rollout: {
            enabled: true,
            canary_percent: 10,
            strategy_whitelist: [],
            account_whitelist: [],
        },
    },
};
const wait = (ms = 120) => new Promise((resolve) => setTimeout(resolve, ms));
const ok = (data) => ({ success: true, data });
const fail = (code, message) => ({
    success: false,
    error: { code, message, retryable: false },
});
const detailsByMode = (mode) => {
    const baseEquity = mode === "sandbox" ? 100000 : 120000;
    return {
        mode,
        currency: "RUB",
        points: Array.from({ length: 8 }).map((_, idx) => {
            const ts = new Date(Date.now() - (7 - idx) * 24 * 60 * 60 * 1000).toISOString();
            const equityDrift = mode === "sandbox" ? idx * 1200 : idx * 700 - idx * 220;
            const noise = mode === "sandbox" ? (idx % 2) * 180 : (idx % 3) * -140;
            const equity = baseEquity + equityDrift + noise;
            const drawdown = mode === "sandbox" ? Math.max(0.02, 0.12 - idx * 0.01) : Math.max(0.05, 0.18 - idx * 0.012);
            return { ts, equity, drawdown };
        }),
    };
};
const regimeSlicesForMode = (mode) => mode === "sandbox"
    ? [
        { regime_id: "range_low_vol", period_fraction: 0.38, return_pct: 1.85, sharpe_ratio: 1.72 },
        { regime_id: "trend_up", period_fraction: 0.3, return_pct: 2.95, sharpe_ratio: 2.05 },
        { regime_id: "trend_down", period_fraction: 0.17, return_pct: -0.65, sharpe_ratio: 0.55 },
        { regime_id: "stress", period_fraction: 0.15, return_pct: -1.2, sharpe_ratio: 0.35 },
    ]
    : [
        { regime_id: "range_low_vol", period_fraction: 0.32, return_pct: 0.55, sharpe_ratio: 0.62 },
        { regime_id: "trend_up", period_fraction: 0.22, return_pct: 1.1, sharpe_ratio: 0.88 },
        { regime_id: "trend_down", period_fraction: 0.26, return_pct: -0.95, sharpe_ratio: 0.42 },
        { regime_id: "stress", period_fraction: 0.2, return_pct: -1.45, sharpe_ratio: 0.28 },
    ];
const computeSummaryMetrics = (points) => {
    if (points.length < 2) {
        return {
            win_rate: 0,
            max_drawdown: 0,
            sharpe_ratio: 0,
            sortino_ratio: 0,
            calmar_ratio: 0,
            cvar_95_pct: 0,
        };
    }
    const equity = points.map((p) => p.equity);
    const rets = [];
    for (let i = 1; i < equity.length; i += 1) {
        const prev = Math.max(equity[i - 1], 1e-9);
        rets.push((equity[i] - equity[i - 1]) / prev);
    }
    const winRate = rets.filter((r) => r > 0).length / rets.length;
    const maxDd = Math.max(...points.map((p) => p.drawdown));
    const mean = rets.reduce((a, b) => a + b, 0) / rets.length;
    const variance = rets.reduce((a, r) => a + (r - mean) ** 2, 0) / rets.length;
    const std = Math.sqrt(Math.max(variance, 1e-18));
    const sharpe = std > 1e-9 ? (mean / std) * Math.sqrt(252) : 0;
    const downsideVar = rets.reduce((a, r) => a + Math.min(0, r) ** 2, 0) / rets.length;
    const downsideStd = Math.sqrt(Math.max(downsideVar, 1e-18));
    const sortino = downsideStd > 1e-9 ? (mean / downsideStd) * Math.sqrt(252) : sharpe;
    const totalRet = (equity[equity.length - 1] - equity[0]) / Math.max(equity[0], 1e-9);
    const periods = equity.length - 1;
    const cagrApprox = periods > 0 ? (1 + totalRet) ** (252 / periods) - 1 : 0;
    const calmar = maxDd > 1e-6 ? cagrApprox / maxDd : 0;
    const sorted = [...rets].sort((a, b) => a - b);
    const k = Math.max(1, Math.ceil(0.05 * sorted.length));
    const tail = sorted.slice(0, k);
    const cvar = -tail.reduce((a, b) => a + b, 0) / k;
    return {
        win_rate: Math.round(winRate * 10000) / 10000,
        max_drawdown: Math.round(maxDd * 10000) / 10000,
        sharpe_ratio: Math.round(sharpe * 1000) / 1000,
        sortino_ratio: Math.round(sortino * 1000) / 1000,
        calmar_ratio: Math.round(calmar * 1000) / 1000,
        cvar_95_pct: Math.round(Math.max(0, cvar) * 10000) / 10000,
    };
};
const summaryByMode = (mode) => {
    const details = detailsByMode(mode);
    const metrics = computeSummaryMetrics(details.points);
    return {
        mode,
        ...metrics,
        regime_slices: regimeSlicesForMode(mode),
    };
};
const abBranchHashBucket = (s) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
        h = (h * 31 + s.charCodeAt(i)) >>> 0;
    }
    return h % 100;
};
const assignAbExperimentBranch = (exp, order) => {
    if (order.mode !== exp.mode)
        return null;
    if (exp.strategy_a_id !== exp.strategy_b_id) {
        if (order.strategy_id === exp.strategy_a_id)
            return "a";
        if (order.strategy_id === exp.strategy_b_id)
            return "b";
        return null;
    }
    if (order.strategy_id !== exp.strategy_a_id)
        return null;
    return abBranchHashBucket(`${exp.id}:${order.id}`) < exp.traffic_split_pct_a ? "a" : "b";
};
const FILLED_ORDER_STATUSES = new Set(["filled", "shadow_filled"]);
const aggregateBranchKpi = (orders) => {
    if (!orders.length) {
        return { pnl: 0, max_drawdown_pct: 0, avg_slippage_bps: 0, reject_rate: 0, stability: 0, trades_count: 0 };
    }
    const sorted = [...orders].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    let pnl = 0;
    const equity = [];
    for (const o of sorted) {
        if (!FILLED_ORDER_STATUSES.has(o.status))
            continue;
        const expP = o.expected_price ?? o.price;
        const realP = o.realized_price ?? o.price;
        const move = realP - expP;
        pnl += o.side === "BUY" ? -move * o.qty : move * o.qty;
        equity.push(pnl);
    }
    let peak = -Infinity;
    let maxDd = 0;
    for (const eq of equity) {
        peak = Math.max(peak, eq);
        const base = Math.max(Math.abs(peak), 1e-9);
        maxDd = Math.max(maxDd, (peak - eq) / base);
    }
    const filled = sorted.filter((o) => FILLED_ORDER_STATUSES.has(o.status));
    const slips = filled.map((o) => o.slippage_bps).filter((x) => typeof x === "number");
    const avgSlip = slips.length ? slips.reduce((a, b) => a + b, 0) / slips.length : 0;
    const rejected = sorted.filter((o) => o.status === "rejected" || (Boolean(o.reject_reason_code) && !FILLED_ORDER_STATUSES.has(o.status))).length;
    const rejectRate = sorted.length ? rejected / sorted.length : 0;
    let stability = 0;
    if (slips.length >= 2) {
        const mean = avgSlip;
        const variance = slips.reduce((acc, x) => acc + (x - mean) ** 2, 0) / slips.length;
        const sd = Math.sqrt(variance);
        stability = Math.max(0, Math.min(1, 1 - sd / 25));
    }
    else if (slips.length === 1) {
        stability = 1;
    }
    return {
        pnl: Math.round(pnl * 100) / 100,
        max_drawdown_pct: Math.round(maxDd * 10000) / 10000,
        avg_slippage_bps: Math.round(avgSlip * 100) / 100,
        reject_rate: Math.round(rejectRate * 10000) / 10000,
        stability: Math.round(stability * 1000) / 1000,
        trades_count: filled.length,
    };
};
const computeAbExperimentKpi = (exp) => {
    const from = new Date(exp.period_from).getTime();
    const to = new Date(exp.period_to).getTime();
    const pool = [...ORDERS, ...SHADOW_ORDERS].filter((o) => {
        if (o.mode !== exp.mode)
            return false;
        const ts = new Date(o.created_at).getTime();
        return ts >= from && ts <= to;
    });
    const branchA = [];
    const branchB = [];
    for (const o of pool) {
        const br = assignAbExperimentBranch(exp, o);
        if (br === "a")
            branchA.push(o);
        else if (br === "b")
            branchB.push(o);
    }
    return {
        experiment_id: exp.id,
        mode: exp.mode,
        branch_a: aggregateBranchKpi(branchA),
        branch_b: aggregateBranchKpi(branchB),
        computed_at: now(),
    };
};
const branchScore = (k) => k.pnl * 3 +
    k.stability * 25 -
    k.avg_slippage_bps * 0.15 -
    k.max_drawdown_pct * 40 -
    k.reject_rate * 20;
const buildAbExperimentWinnerSuggestion = (exp, kpi) => {
    const a = kpi.branch_a;
    const b = kpi.branch_b;
    const score_branch_a = branchScore(a);
    const score_branch_b = branchScore(b);
    const minT = exp.stop_criteria.min_trades_per_branch;
    const maxDdLim = exp.stop_criteria.max_drawdown_limit_pct;
    const minSlip = exp.stop_criteria.min_slippage_improvement_pct;
    const readyTrades = a.trades_count >= minT && b.trades_count >= minT;
    const readyDd = a.max_drawdown_pct <= maxDdLim && b.max_drawdown_pct <= maxDdLim;
    const slipDenom = Math.max(a.avg_slippage_bps, 1e-6);
    const slipImprove = (a.avg_slippage_bps - b.avg_slippage_bps) / slipDenom;
    const readySlip = slipImprove >= minSlip || (a.avg_slippage_bps === 0 && b.avg_slippage_bps === 0);
    const auto_rule_ready = readyTrades && readyDd && readySlip;
    const reasons = [`scores: A=${score_branch_a.toFixed(2)}, B=${score_branch_b.toFixed(2)}`];
    if (!readyTrades)
        reasons.push(`trades A=${a.trades_count}, B=${b.trades_count}, need >=${minT}`);
    if (!readyDd)
        reasons.push(`max_dd limit=${maxDdLim}: A=${a.max_drawdown_pct.toFixed(4)}, B=${b.max_drawdown_pct.toFixed(4)}`);
    if (!readySlip)
        reasons.push(`slippage improve vs A=${slipImprove.toFixed(4)}, min=${minSlip}`);
    let suggested_branch = "none";
    if (auto_rule_ready) {
        const diff = score_branch_a - score_branch_b;
        if (Math.abs(diff) < 0.5)
            suggested_branch = "tie";
        else
            suggested_branch = diff > 0 ? "a" : "b";
    }
    return {
        experiment_id: exp.id,
        suggested_branch,
        score_branch_a: Math.round(score_branch_a * 100) / 100,
        score_branch_b: Math.round(score_branch_b * 100) / 100,
        reasons,
        auto_rule_ready,
        computed_at: now(),
    };
};
const cloneAbKpi = (k) => ({
    ...k,
    branch_a: { ...k.branch_a },
    branch_b: { ...k.branch_b },
});
const cloneAbSuggestion = (s) => ({
    ...s,
    reasons: [...s.reasons],
});
const abCriteriaFingerprint = (exp, kpi, sug) => {
    const parts = [
        exp.stop_criteria.min_trades_per_branch,
        exp.stop_criteria.max_drawdown_limit_pct,
        exp.stop_criteria.min_slippage_improvement_pct,
        kpi.branch_a.trades_count,
        kpi.branch_b.trades_count,
        Math.round(kpi.branch_a.pnl * 1000),
        Math.round(kpi.branch_b.pnl * 1000),
        Math.round(kpi.branch_a.avg_slippage_bps * 100),
        Math.round(kpi.branch_b.avg_slippage_bps * 100),
        Math.round(sug.score_branch_a * 100),
        Math.round(sug.score_branch_b * 100),
        sug.suggested_branch,
        String(sug.auto_rule_ready),
    ];
    return parts.join("|");
};
const nextAbLogId = (experimentId) => {
    const n = AB_EXPERIMENT_DECISION_LOGS[experimentId]?.length ?? 0;
    return `ablog-${experimentId}-${n + 1}-${Date.now()}`;
};
const pushAbDecisionLogEntry = (entry) => {
    if (!AB_EXPERIMENT_DECISION_LOGS[entry.experiment_id]) {
        AB_EXPERIMENT_DECISION_LOGS[entry.experiment_id] = [];
    }
    AB_EXPERIMENT_DECISION_LOGS[entry.experiment_id].push(entry);
};
export class MockRestApi {
    getSessionUserFromStore() {
        if (typeof localStorage === "undefined")
            return null;
        const raw = localStorage.getItem("itr_auth_session");
        if (!raw)
            return null;
        try {
            const s = JSON.parse(raw);
            const id = s.user?.id;
            if (!id)
                return null;
            const fromTable = USERS.find((u) => u.id === id);
            return fromTable ? { ...fromTable } : { ...s.user };
        }
        catch {
            return null;
        }
    }
    /** Блокировка эндпоинтов торгового контура для не-`active` учётных записей. */
    tradingOrForbidden() {
        const u = this.getSessionUserFromStore();
        if (!u)
            return fail("UNAUTHENTICATED", "Сессия не найдена");
        if (u.activation_status !== "active") {
            return fail("FORBIDDEN_NOT_ACTIVE", "Торговый контур доступен только для активированной учётной записи.");
        }
        return null;
    }
    adminOrForbidden() {
        const u = this.getSessionUserFromStore();
        if (!u)
            return fail("UNAUTHENTICATED", "Сессия не найдена");
        if (!u.roles.includes("admin")) {
            return fail("FORBIDDEN", "Недостаточно прав (нужна роль admin).");
        }
        return null;
    }
    orderNotional(input) {
        const lot = resolveLotSize(input.ticker ?? "", input.lot_size);
        return orderNotionalFromLots(input.qty, lot, input.price);
    }
    modeNotionalUsedToday(mode) {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const from = start.getTime();
        return ORDERS.filter((order) => order.mode === mode && new Date(order.created_at).getTime() >= from)
            .reduce((acc, order) => acc + this.orderNotional(order), 0);
    }
    tickerExposureNotional(mode, ticker) {
        return POSITIONS.filter((position) => position.mode === mode && position.ticker === ticker).reduce((acc, position) => acc + Math.abs(position.qty * position.market_price), 0);
    }
    strategyNotionalUsedToday(mode, strategyId) {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const from = start.getTime();
        return ORDERS.filter((order) => order.mode === mode &&
            order.strategy_id === strategyId &&
            new Date(order.created_at).getTime() >= from).reduce((acc, order) => acc + this.orderNotional(order), 0);
    }
    clusterNotionalUsedToday(mode, clusterKey) {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const from = start.getTime();
        return ORDERS.filter((order) => {
            if (order.mode !== mode)
                return false;
            if (new Date(order.created_at).getTime() < from)
                return false;
            return CLUSTER_BY_TICKER[order.ticker] === clusterKey;
        }).reduce((acc, order) => acc + this.orderNotional(order), 0);
    }
    netExposureNotionalAfterOrder(input) {
        const positionsNet = POSITIONS.filter((position) => position.mode === input.mode).reduce((acc, position) => acc + position.qty * position.market_price, 0);
        const orderSigned = (input.side === "BUY" ? 1 : -1) * this.orderNotional(input);
        return Math.abs(positionsNet + orderSigned);
    }
    grossExposureNotionalAfterOrder(input) {
        const positionsGross = POSITIONS.filter((position) => position.mode === input.mode).reduce((acc, position) => acc + Math.abs(position.qty * position.market_price), 0);
        return positionsGross + this.orderNotional(input);
    }
    /** Net exposure после гипотетической сделки с заданным notional (для post-trade check по фактической цене). */
    netExposureNotionalWithOrderNotional(input, notionalAbs) {
        const positionsNet = POSITIONS.filter((position) => position.mode === input.mode).reduce((acc, position) => acc + position.qty * position.market_price, 0);
        const orderSigned = (input.side === "BUY" ? 1 : -1) * notionalAbs;
        return Math.abs(positionsNet + orderSigned);
    }
    recordPortfolioOrderAttempt(mode) {
        PORTFOLIO_KPI[mode].order_attempts += 1;
    }
    recordPortfolioRejectIfNeeded(mode, code) {
        if (!code.startsWith("PORTFOLIO_LIMIT_"))
            return;
        const st = PORTFOLIO_KPI[mode];
        st.portfolio_rejections += 1;
        st.by_code[code] = (st.by_code[code] ?? 0) + 1;
    }
    resolveExecutionProfile(input) {
        if (input.execution_profile) {
            return input.execution_profile;
        }
        return STRATEGY_POLICIES[input.strategy_id]?.execution_profile ?? "balanced";
    }
    resolveExecutionProfileWithAutoTwap(input, notional) {
        const base = this.resolveExecutionProfile(input);
        const policy = STRATEGY_POLICIES[input.strategy_id];
        return applyAutoTwapProfile(base, notional, policy?.min_liquidity_notional ?? 0);
    }
    /**
     * При мягкой деградации MD переключение на следующую площадку по `venue_failover_order` и критерию.
     * Иначе — `primary_venue`.
     */
    resolveExecutionVenue(ex, mildDegraded) {
        if (!mildDegraded || ex.venue_failover_order.length === 0) {
            return ex.primary_venue;
        }
        const f = ex.venue_failover_order;
        switch (ex.venue_switch_criterion) {
            case "lowest_latency":
                return f[0];
            case "min_spread_bps":
                return f[Math.min(f.length - 1, 1)];
            case "max_top_of_book":
                return f[f.length - 1];
            default:
                return f[0];
        }
    }
    shouldAutoShadowByRollout(input, accountId) {
        if (input.mode !== "live")
            return false;
        const rollout = RUNTIME_POLICIES.live.shadow_rollout;
        if (!rollout.enabled)
            return false;
        if (rollout.strategy_whitelist.includes(input.strategy_id))
            return true;
        if (accountId && rollout.account_whitelist.includes(accountId))
            return true;
        const canary = Math.max(0, Math.min(100, rollout.canary_percent));
        return Math.random() * 100 < canary;
    }
    appendRejectedTrace(input, reasonCode, detail) {
        const started = now();
        TRACE_ENTRIES.unshift({
            id: `tr-pretrade-${Date.now()}`,
            mode: input.mode,
            correlation_id: `corr-pretrade-${Date.now()}`,
            strategy_id: input.strategy_id,
            ticker: input.ticker,
            started_at: started,
            steps: [
                { stage: "signal", status: "ok", at: started, detail: `${input.side} ${input.ticker}` },
                { stage: "risk_gate", status: "rejected", at: now(), detail, reason_code: reasonCode },
                { stage: "router", status: "pending", at: now(), detail: "—" },
                { stage: "venue", status: "pending", at: now(), detail: "—" },
            ],
        });
        TRACE_ENTRIES.splice(200);
    }
    appendAcceptedTrace(order) {
        const started = order.created_at || now();
        const filled = order.status === "filled" || order.status === "shadow_filled";
        const venueDetail = order.status === "shadow_filled"
            ? `Shadow fill${order.realized_price != null ? ` @ ${order.realized_price}` : ""}`
            : filled
                ? `Venue fill${order.realized_price != null ? ` @ ${order.realized_price}` : ""}`
                : "Awaiting venue fill";
        const liq = order.cancel_replace_last?.liquidity;
        let riskDetail = "Pre-trade checks passed";
        if (liq?.top_of_book_notional != null) {
            const top = Math.round(liq.top_of_book_notional);
            const min = liq.min_liquidity_notional != null ? Math.round(liq.min_liquidity_notional) : null;
            riskDetail =
                min != null
                    ? `${riskDetail} · ликвидность лучшего уровня ${top} (порог ${min})`
                    : `${riskDetail} · ликвидность лучшего уровня ${top}`;
            if (liq.observed_spread_bps != null) {
                riskDetail += ` · спред ${Number(liq.observed_spread_bps).toFixed(1)} bps`;
            }
        }
        const existing = TRACE_ENTRIES.find((entry) => entry.correlation_id === order.id);
        const steps = [
            { stage: "signal", status: "ok", at: started, detail: `${order.side} ${order.ticker}` },
            { stage: "risk_gate", status: "ok", at: started, detail: riskDetail },
            {
                stage: "router",
                status: "ok",
                at: now(),
                detail: order.is_shadow
                    ? `SHADOW route → ${order.execution_venue}: ${order.type} @ ${order.price}`
                    : `Router → ${order.execution_venue}: ${order.type} @ ${order.price}`,
            },
            {
                stage: "venue",
                status: (filled ? "ok" : "pending"),
                at: now(),
                detail: venueDetail,
            },
        ];
        if (existing) {
            existing.steps = steps;
            existing.is_shadow = order.is_shadow === true;
            return;
        }
        TRACE_ENTRIES.unshift({
            id: `tr-order-${Date.now()}`,
            mode: order.mode,
            correlation_id: order.id,
            strategy_id: order.strategy_id,
            ticker: order.ticker,
            started_at: started,
            is_shadow: order.is_shadow === true,
            steps,
        });
        TRACE_ENTRIES.splice(200);
    }
    checkKillSwitch(input) {
        if (KILL_SWITCH_STATE.account) {
            return { ok: false, code: "PRETRADE_KILL_SWITCH_ACCOUNT", message: "Kill switch уровня account активирован." };
        }
        if (KILL_SWITCH_STATE.mode[input.mode]) {
            return { ok: false, code: "PRETRADE_KILL_SWITCH_MODE", message: `Kill switch режима ${input.mode} активирован.` };
        }
        if (KILL_SWITCH_STATE.strategy[input.strategy_id]) {
            return { ok: false, code: "PRETRADE_KILL_SWITCH_STRATEGY", message: `Kill switch стратегии ${input.strategy_id} активирован.` };
        }
        return { ok: true };
    }
    maybeAutoDeactivateStrategy(strategyId, mode) {
        REJECT_STREAK_BY_STRATEGY[strategyId] = (REJECT_STREAK_BY_STRATEGY[strategyId] ?? 0) + 1;
        if (REJECT_STREAK_BY_STRATEGY[strategyId] < RUNTIME_POLICIES[mode].posttrade.auto_deactivate_reject_streak)
            return;
        const strategy = STRATEGIES.find((item) => item.id === strategyId);
        if (strategy) {
            strategy.is_active = false;
        }
        KILL_SWITCH_STATE.strategy[strategyId] = true;
        TRACE_ENTRIES.unshift({
            id: `tr-autodeactivate-${Date.now()}`,
            mode,
            correlation_id: `corr-autodeactivate-${Date.now()}`,
            strategy_id: strategyId,
            ticker: "—",
            started_at: now(),
            steps: [
                { stage: "signal", status: "ok", at: now(), detail: `Auto action for ${strategyId}` },
                {
                    stage: "risk_gate",
                    status: "rejected",
                    at: now(),
                    detail: "Стратегия авто-деактивирована, включён strategy kill switch.",
                    reason_code: "POSTTRADE_AUTO_DEACTIVATED",
                },
                { stage: "router", status: "pending", at: now(), detail: "—" },
                { stage: "venue", status: "pending", at: now(), detail: "—" },
            ],
        });
        TRACE_ENTRIES.splice(200);
    }
    runPreTradeChecks(input) {
        const risk = RISK_POLICIES[input.mode];
        const strategyPolicy = STRATEGY_POLICIES[input.strategy_id];
        const runtime = RUNTIME_POLICIES[input.mode];
        const strategy = STRATEGIES.find((item) => item.id === input.strategy_id);
        const ticker = input.ticker.toUpperCase();
        const nowTs = Date.now();
        const kill = this.checkKillSwitch(input);
        if (!kill.ok)
            return kill;
        const primaryStaleMs = nowTs - MARKET_DATA_HEALTH[input.mode].last_primary_at;
        const fallbackStaleMs = nowTs - MARKET_DATA_HEALTH[input.mode].last_fallback_at;
        if (runtime.market_data.block_trading_on_degraded &&
            primaryStaleMs > runtime.market_data.primary_stale_ms &&
            fallbackStaleMs > runtime.market_data.fallback_stale_ms) {
            return {
                ok: false,
                code: "PRETRADE_MARKET_DATA_DEGRADED",
                message: "Рыночные данные деградированы: heartbeat stale и fallback недоступен.",
            };
        }
        if (COOLDOWN_UNTIL_BY_MODE[input.mode] > nowTs) {
            const sec = Math.max(1, Math.ceil((COOLDOWN_UNTIL_BY_MODE[input.mode] - nowTs) / 1000));
            return { ok: false, code: "PRETRADE_LIMIT_COOLDOWN", message: `Активен cooldown после серии убытков (${sec}s).` };
        }
        // MA Cross reentry cooldown: после MA-SELL (signal_source=ma_cross) следующий auto-BUY этого тикера блокируем
        // на несколько минут, чтобы не входить сразу на отскоке.
        if (input.side === "BUY" && input.signal_source === "ma_cross") {
            const cooldownMinRaw = strategy?.params?.ma_reentry_cooldown_min;
            const cooldownMin = Number.isFinite(cooldownMinRaw) ? Number(cooldownMinRaw) : 3;
            const cooldownMs = Math.max(0, cooldownMin) * 60 * 1000;
            if (cooldownMs > 0) {
                const lastSell = [...ORDERS]
                    .reverse()
                    .find((o) => o.mode === input.mode &&
                    o.strategy_id === input.strategy_id &&
                    o.ticker.toUpperCase() === ticker &&
                    o.side === "SELL" &&
                    o.status === "filled" &&
                    o.signal_source === "ma_cross");
                if (lastSell) {
                    const lastSellAt = typeof lastSell.created_at === "string" ? Date.parse(lastSell.created_at) : Date.now();
                    if (nowTs < lastSellAt + cooldownMs) {
                        const sec = Math.max(1, Math.ceil((lastSellAt + cooldownMs - nowTs) / 1000));
                        return {
                            ok: false,
                            code: "PRETRADE_MA_REENTRY_COOLDOWN",
                            message: `Активен MA-reentry cooldown по ${ticker} (${sec}s).`,
                        };
                    }
                }
            }
        }
        if (strategyPolicy && strategyPolicy.mode === input.mode) {
            if (strategyPolicy.allowed_tickers.length > 0 && !strategyPolicy.allowed_tickers.includes(ticker)) {
                return { ok: false, code: "PRETRADE_LIMIT_TICKER_NOT_ALLOWED", message: "Тикер не входит в allow-list стратегии." };
            }
            const buyLocked = (strategyPolicy.tickers ?? []).some((item) => item.ticker === ticker && item.lock_buy);
            if (input.side === "BUY" && buyLocked) {
                return {
                    ok: false,
                    code: "PRETRADE_TICKER_BUY_LOCKED",
                    message: "Покупки по тикеру заблокированы политикой стратегии (lock_buy).",
                };
            }
            const session = isWithinTradingSession(strategyPolicy.trading_schedule);
            if (!session.ok) {
                return { ok: false, code: "PRETRADE_OUTSIDE_TRADING_SESSION", message: session.message };
            }
        }
        const schedule = buildFallbackExchangeSchedule(input.mode);
        schedule.agent_schedule = { ...USER_TRADING_PREFS.schedule };
        const tradability = mockInstrumentTradability(ticker, input.type, schedule, USER_TRADING_PREFS.schedule);
        if (tradability.tradable_now === false) {
            return {
                ok: false,
                code: "PRETRADE_INSTRUMENT_NOT_TRADABLE",
                message: tradability.reason ?? "Инструмент недоступен для торговли.",
            };
        }
        if (risk.allowed_tickers.length > 0 && !risk.allowed_tickers.includes(ticker)) {
            return { ok: false, code: "PRETRADE_LIMIT_TICKER_NOT_ALLOWED", message: "Тикер не разрешён risk-политикой." };
        }
        if (risk.blocked_tickers.includes(ticker)) {
            return { ok: false, code: "PRETRADE_LIMIT_TICKER_BLOCKED", message: "Тикер заблокирован risk-политикой." };
        }
        if (input.side === "SELL") {
            const sameTickerPosition = POSITIONS.find((position) => position.mode === input.mode && position.ticker === ticker);
            const availableQty = sameTickerPosition?.qty ?? 0;
            if (availableQty < input.qty) {
                return {
                    ok: false,
                    code: "PRETRADE_INSUFFICIENT_POSITION",
                    message: `Недостаточно бумаг для продажи по ${ticker}: доступно ${availableQty}, запрошено ${input.qty}.`,
                };
            }
            if (ALL_SELLS_BLOCKED_TICKERS[input.mode].has(ticker)) {
                return {
                    ok: false,
                    code: "PRETRADE_ALL_SELLS_BLOCKED",
                    message: `Все продажи по ${ticker} временно заблокированы. Снимите блокировку на карточке позиции.`,
                };
            }
            if (input.signal_source === "stop_loss" &&
                SELL_LOCKED_TICKERS[input.mode].has(ticker)) {
                return {
                    ok: false,
                    code: "PRETRADE_SELL_LOCKED",
                    message: `Stop loss по ${ticker} временно заблокирован. Ручная продажа и take profit разрешены.`,
                };
            }
        }
        const sameTickerPosition = POSITIONS.find((position) => position.mode === input.mode && position.ticker === ticker);
        // Убыточное усреднение запрещено, кроме явного сигнала стратегии loss_avg.
        if (sameTickerPosition && sameTickerPosition.pnl < 0 && input.signal_source !== "loss_avg") {
            const direction = sameTickerPosition.qty >= 0 ? "BUY" : "SELL";
            if (direction === input.side) {
                return {
                    ok: false,
                    code: "PRETRADE_LIMIT_AVERAGING_POLICY",
                    message: "Запрещено усреднение убыточной позиции вне политики.",
                };
            }
        }
        const notional = this.orderNotional(input);
        const portfolio = PORTFOLIO_LIMITS[input.mode];
        const totalBudgetUsed = this.modeNotionalUsedToday(input.mode);
        if (totalBudgetUsed + notional > portfolio.total_budget_notional) {
            return {
                ok: false,
                code: "PORTFOLIO_LIMIT_TOTAL_BUDGET",
                message: "Превышен общий бюджет портфеля.",
            };
        }
        const strategyBudget = portfolio.strategy_budgets.find((item) => item.key === input.strategy_id);
        if (strategyBudget) {
            const strategyUsed = this.strategyNotionalUsedToday(input.mode, input.strategy_id);
            if (strategyUsed + notional > strategyBudget.limit_notional) {
                return {
                    ok: false,
                    code: "PORTFOLIO_LIMIT_STRATEGY_BUDGET",
                    message: `Превышен бюджет стратегии ${input.strategy_id}.`,
                };
            }
        }
        const clusterKey = CLUSTER_BY_TICKER[ticker];
        if (clusterKey) {
            const clusterBudget = portfolio.cluster_budgets.find((item) => item.key === clusterKey);
            if (clusterBudget) {
                const clusterUsed = this.clusterNotionalUsedToday(input.mode, clusterKey);
                if (clusterUsed + notional > clusterBudget.limit_notional) {
                    return {
                        ok: false,
                        code: "PORTFOLIO_LIMIT_CLUSTER_BUDGET",
                        message: `Превышен бюджет кластера ${clusterKey}.`,
                    };
                }
            }
        }
        const netAfter = this.netExposureNotionalAfterOrder(input);
        if (netAfter > portfolio.net_exposure_cap_notional) {
            return {
                ok: false,
                code: "PORTFOLIO_LIMIT_NET_EXPOSURE",
                message: "Превышен лимит net exposure портфеля.",
            };
        }
        const grossAfter = this.grossExposureNotionalAfterOrder(input);
        if (grossAfter > portfolio.gross_exposure_cap_notional) {
            return {
                ok: false,
                code: "PORTFOLIO_LIMIT_GROSS_EXPOSURE",
                message: "Превышен лимит gross exposure портфеля.",
            };
        }
        if (notional > risk.max_position_notional) {
            return { ok: false, code: "PRETRADE_LIMIT_MAX_ORDER_SIZE", message: "Превышен лимит max order size." };
        }
        const referencePrice = LAST_MARKET_PRICE[input.mode]?.[ticker] ??
            POSITIONS.find((p) => p.mode === input.mode && p.ticker === ticker)?.market_price;
        if (referencePrice && referencePrice > 0) {
            const deltaPct = Math.abs(input.price - referencePrice) / referencePrice;
            if (deltaPct > runtime.pretrade.price_band_pct) {
                return { ok: false, code: "PRETRADE_LIMIT_PRICE_BAND", message: "Цена заявки вне допустимого price band." };
            }
        }
        const tickerExposure = this.tickerExposureNotional(input.mode, ticker);
        // Reduce-only SELL уменьшает экспозицию — не суммируем нотионал.
        const lotSize = resolveLotSize(ticker);
        const sharesRequested = input.qty * lotSize;
        const positionQty = POSITIONS.filter((p) => p.mode === input.mode && p.ticker === ticker).reduce((acc, p) => acc + p.qty, 0);
        const isReduceOnly = input.side === "SELL" && sharesRequested > 0 && sharesRequested <= positionQty;
        if (!isReduceOnly && tickerExposure + notional > risk.max_exposure_per_ticker) {
            return { ok: false, code: "PRETRADE_LIMIT_TICKER_EXPOSURE", message: "Превышен лимит экспозиции по тикеру." };
        }
        const dailyNotionalUsed = this.modeNotionalUsedToday(input.mode);
        if (!isReduceOnly && dailyNotionalUsed + notional > risk.max_notional_per_day) {
            return { ok: false, code: "PRETRADE_LIMIT_DAILY_NOTIONAL", message: "Превышен дневной лимит нотионала." };
        }
        const limits = RISK_LIMITS[input.mode];
        const tradingOrders = [...ORDERS, ...SHADOW_ORDERS].filter((o) => o.mode === input.mode).map((o) => withOrderLots(o));
        const tradingPnl = buildTradingPnlBreakdown(tradingOrders, 0);
        const dailyLossUsed = Math.max(0, -tradingPnl.realized_pnl_today);
        if (dailyLossUsed >= limits.max_daily_loss) {
            if (!isReduceOnly) {
                return { ok: false, code: "PRETRADE_LIMIT_DAILY_LOSS", message: "Превышен дневной лимит убытка." };
            }
            const pos = POSITIONS.find((p) => p.mode === input.mode && p.ticker === ticker);
            const avg = pos?.avg_price ?? 0;
            const commissionPct = risk.commission_rate_pct ?? 0.3;
            const sellRate = Math.max(0, commissionPct) / 100;
            const breakeven = avg > 0 && sellRate < 1 ? avg / (1 - sellRate) : avg;
            if (!(avg > 0 && input.price >= breakeven)) {
                return {
                    ok: false,
                    code: "PRETRADE_LIMIT_DAILY_LOSS",
                    message: "Превышен дневной лимит убытка: закрытие только в плюс (stop-loss не исключается).",
                };
            }
        }
        return { ok: true };
    }
    recordMarketDataHeartbeat(mode, source = "primary") {
        if (source === "primary") {
            MARKET_DATA_HEALTH[mode].last_primary_at = Date.now();
        }
        else {
            MARKET_DATA_HEALTH[mode].last_fallback_at = Date.now();
        }
        MARKET_DATA_HEALTH[mode].last_source = source;
    }
    setMarketDataHeartbeatForTests(mode, source, timestampMs) {
        if (source === "primary") {
            MARKET_DATA_HEALTH[mode].last_primary_at = timestampMs;
        }
        else {
            MARKET_DATA_HEALTH[mode].last_fallback_at = timestampMs;
        }
        MARKET_DATA_HEALTH[mode].last_source = source;
    }
    /**
     * Умеренная деградация: primary сильно stale, fallback ещё в допуске (противоположно полному `PRETRADE_MARKET_DATA_DEGRADED`).
     */
    isExecutionMildMarketDataDegradation(mode) {
        const rt = RUNTIME_POLICIES[mode];
        const h = MARKET_DATA_HEALTH[mode];
        const nowTs = Date.now();
        const primaryStaleMs = nowTs - h.last_primary_at;
        const fallbackStaleMs = nowTs - h.last_fallback_at;
        return (primaryStaleMs > rt.market_data.primary_stale_ms * 0.45 &&
            fallbackStaleMs <= rt.market_data.fallback_stale_ms);
    }
    /** Сброс счётчиков портфельных KPI (для тестов). */
    resetPortfolioKpiForTests() {
        ["sandbox", "live"].forEach((m) => {
            PORTFOLIO_KPI[m] = createEmptyKpiState();
        });
    }
    async login(email, _password) {
        await wait();
        const found = USERS.find((u) => u.email.toLowerCase() === email.trim().toLowerCase());
        if (!found) {
            return fail("INVALID_CREDENTIALS", "Пользователь с таким email не найден (мок-режим: используйте заранее заведённый email).");
        }
        return ok({
            access_token: "mock-access-token",
            refresh_token: "mock-refresh-token",
            expires_in: 3600,
            user: { ...found },
        });
    }
    async register(email, _password, name) {
        await wait();
        const normalized = email.trim().toLowerCase();
        if (USERS.some((u) => u.email.toLowerCase() === normalized)) {
            return fail("EMAIL_TAKEN", "Уже есть учётная запись с таким email.");
        }
        const nu = {
            id: `u-reg-${Date.now()}`,
            email: normalized,
            name: name.trim() || "User",
            activation_status: "pending_activation",
            roles: ["trader"],
            registered_at: now(),
        };
        USERS.push(nu);
        return ok({
            access_token: "mock-access-token",
            refresh_token: "mock-refresh-token",
            expires_in: 3600,
            user: { ...nu },
        });
    }
    async refresh(_refreshToken) {
        await wait();
        const u = this.getSessionUserFromStore();
        if (!u)
            return fail("UNAUTHENTICATED", "Сессия не найдена");
        return ok({
            access_token: "mock-access-token-next",
            refresh_token: "mock-refresh-token-next",
            expires_in: 3600,
            user: { ...u },
        });
    }
    async me() {
        await wait();
        const u = this.getSessionUserFromStore();
        if (!u)
            return fail("UNAUTHENTICATED", "Сессия не найдена");
        return ok({ ...u });
    }
    async listTokens() {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok([...TOKENS]);
    }
    async createToken(input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        if (!input.token.trim())
            return fail("VALIDATION_ERROR", "Значение токена обязательно");
        const issued = now();
        const expires = brokerTokenExpiresAt(issued);
        const token = {
            id: `t${TOKENS.length + 1}`,
            token: input.token.trim(),
            mode: input.mode,
            is_valid: input.mode === "sandbox",
            updated_at: issued,
            issued_at: issued,
            expires_at: expires,
            rotate_until_at: expires,
            last_validated_at: undefined,
            last_validation_outcome: "never",
            rotation_zero_downtime: true,
        };
        TOKENS.push(token);
        return ok({ ...token });
    }
    async validateToken(id) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const token = TOKENS.find((item) => item.id === id);
        if (!token)
            return fail("NOT_FOUND", "Токен не найден");
        const validatedAt = now();
        if (token.mode === "live") {
            token.is_valid = false;
            token.updated_at = validatedAt;
            token.last_validated_at = validatedAt;
            token.last_validation_outcome = "failed";
            return fail("TOKEN_INVALID", "Live токен не прошел проверку");
        }
        token.is_valid = true;
        token.updated_at = validatedAt;
        token.last_validated_at = validatedAt;
        token.last_validation_outcome = "ok";
        token.broker_snapshot = {
            fetched_at: validatedAt,
            accounts_count: 1,
            prem_status: false,
            qual_status: false,
            tariff: "mock-trader",
            qualified_for_work_with: ["bond", "share"],
        };
        return ok({ ...token });
    }
    async updateToken(id, input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const token = TOKENS.find((item) => item.id === id);
        if (!token)
            return fail("NOT_FOUND", "Токен не найден");
        if (typeof input.token === "string") {
            const next = input.token.trim();
            if (!next)
                return fail("VALIDATION_ERROR", "Значение токена обязательно");
            if (next.startsWith("****") || next === "****") {
                return fail("VALIDATION_ERROR", "Передано замаскированное значение токена. Вставьте полный секрет заново.");
            }
            if (next !== token.token && token.rotation_zero_downtime) {
                const issued = now();
                token.issued_at = issued;
                token.expires_at = brokerTokenExpiresAt(issued);
                token.rotate_until_at = token.expires_at;
                token.last_validated_at = undefined;
                token.last_validation_outcome = "never";
                token.is_valid = false;
            }
            token.token = next;
        }
        if (typeof input.is_valid === "boolean")
            token.is_valid = input.is_valid;
        token.updated_at = now();
        return ok({ ...token });
    }
    async removeToken(id) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const index = TOKENS.findIndex((item) => item.id === id);
        if (index === -1)
            return fail("NOT_FOUND", "Токен не найден");
        TOKENS.splice(index, 1);
        return ok({ id });
    }
    async listStrategies(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok(STRATEGIES.filter((s) => s.mode === mode).map((s) => ({ ...s })));
    }
    async createStrategy(input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const strategy = {
            id: `s${STRATEGIES.length + 1}`,
            mode: input.mode,
            name: input.name,
            is_active: false,
            params: input.params,
        };
        STRATEGIES.push(strategy);
        STRATEGY_VERSIONS.unshift({
            id: `sv${Date.now()}`,
            strategy_id: strategy.id,
            mode: strategy.mode,
            created_at: now(),
            author: "Trader",
            params: { ...strategy.params },
        });
        STRATEGY_POLICIES[strategy.id] = mergeStrategyPolicyRow({
            strategy_id: strategy.id,
            mode: strategy.mode,
            priority: 50,
            conflict_policy: "skip",
            allowed_tickers: [],
            execution_profile: "balanced",
            updated_at: now(),
        });
        return ok({ ...strategy });
    }
    async updateStrategy(id, input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const strategy = STRATEGIES.find((item) => item.id === id);
        if (!strategy)
            return fail("NOT_FOUND", "Стратегия не найдена");
        if (input.name != null)
            strategy.name = input.name.trim() || strategy.name;
        if (input.params)
            strategy.params = input.params;
        STRATEGY_VERSIONS.unshift({
            id: `sv${Date.now()}`,
            strategy_id: strategy.id,
            mode: strategy.mode,
            created_at: now(),
            author: "Trader",
            params: { ...strategy.params },
        });
        return ok({ ...strategy });
    }
    async removeStrategy(id) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const index = STRATEGIES.findIndex((item) => item.id === id);
        if (index === -1)
            return fail("NOT_FOUND", "Стратегия не найдена");
        STRATEGIES.splice(index, 1);
        delete STRATEGY_POLICIES[id];
        return ok({ id });
    }
    async activateStrategy(id) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const strategy = STRATEGIES.find((item) => item.id === id);
        if (!strategy)
            return fail("NOT_FOUND", "Стратегия не найдена");
        strategy.is_active = true;
        return ok({ ...strategy });
    }
    async deactivateStrategy(id) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const strategy = STRATEGIES.find((item) => item.id === id);
        if (!strategy)
            return fail("NOT_FOUND", "Стратегия не найдена");
        strategy.is_active = false;
        return ok({ ...strategy });
    }
    async cloneStrategy(id, targetMode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const strategy = STRATEGIES.find((item) => item.id === id);
        if (!strategy)
            return fail("NOT_FOUND", "Стратегия не найдена");
        const cloned = {
            ...strategy,
            id: `s${STRATEGIES.length + 1}`,
            mode: targetMode,
            name: `${strategy.name} (${targetMode})`,
            is_active: false,
        };
        STRATEGIES.push(cloned);
        STRATEGY_VERSIONS.unshift({
            id: `sv${Date.now()}`,
            strategy_id: cloned.id,
            mode: cloned.mode,
            created_at: now(),
            author: "Trader",
            params: { ...cloned.params },
        });
        STRATEGY_POLICIES[cloned.id] = mergeStrategyPolicyRow({
            strategy_id: cloned.id,
            mode: cloned.mode,
            priority: 50,
            conflict_policy: "skip",
            allowed_tickers: [],
            execution_profile: "balanced",
            updated_at: now(),
        });
        return ok({ ...cloned });
    }
    async listStrategyVersions(id) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok(STRATEGY_VERSIONS.filter((version) => version.strategy_id === id).map((version) => ({ ...version })));
    }
    async rollbackStrategy(id, versionId) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const strategy = STRATEGIES.find((item) => item.id === id);
        if (!strategy)
            return fail("NOT_FOUND", "Стратегия не найдена");
        const version = STRATEGY_VERSIONS.find((item) => item.id === versionId && item.strategy_id === id);
        if (!version)
            return fail("NOT_FOUND", "Версия стратегии не найдена");
        strategy.params = { ...version.params };
        STRATEGY_VERSIONS.unshift({
            id: `sv${Date.now()}`,
            strategy_id: strategy.id,
            mode: strategy.mode,
            created_at: now(),
            author: "Trader",
            params: { ...strategy.params },
        });
        return ok({ ...strategy });
    }
    async listStrategyPresets(_mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok(STRATEGY_PRESETS.map((preset) => ({ ...preset, params: { ...preset.params } })));
    }
    async listAbExperiments(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok(AB_EXPERIMENTS.filter((exp) => exp.mode === mode).map((exp) => ({
            ...exp,
            stop_criteria: { ...exp.stop_criteria },
            ...(exp.decision ? { decision: { ...exp.decision } } : {}),
        })));
    }
    async createAbExperiment(input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        if (!STRATEGIES.some((s) => s.id === input.strategy_a_id))
            return fail("NOT_FOUND", "Стратегия A не найдена");
        if (!STRATEGIES.some((s) => s.id === input.strategy_b_id))
            return fail("NOT_FOUND", "Стратегия B не найдена");
        const exp = {
            id: `ab-exp-${Date.now()}`,
            ...input,
            stop_criteria: { ...input.stop_criteria },
            status: "draft",
            created_at: now(),
            updated_at: now(),
        };
        AB_EXPERIMENTS.unshift(exp);
        AB_EXPERIMENTS.splice(100);
        pushAbDecisionLogEntry({
            id: nextAbLogId(exp.id),
            experiment_id: exp.id,
            mode: exp.mode,
            at: exp.created_at,
            actor: "user",
            event_type: "experiment_created",
            summary: `Создан эксперимент «${exp.name}»`,
        });
        return ok({ ...exp, stop_criteria: { ...exp.stop_criteria } });
    }
    async updateAbExperiment(id, input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const exp = AB_EXPERIMENTS.find((item) => item.id === id);
        if (!exp)
            return fail("NOT_FOUND", "Эксперимент не найден");
        const prevStatus = exp.status;
        Object.assign(exp, {
            ...input,
            stop_criteria: input.stop_criteria ? { ...input.stop_criteria } : exp.stop_criteria,
            updated_at: now(),
        });
        if (input.status !== undefined && input.status !== prevStatus) {
            pushAbDecisionLogEntry({
                id: nextAbLogId(exp.id),
                experiment_id: exp.id,
                mode: exp.mode,
                at: now(),
                actor: "user",
                event_type: "experiment_status_changed",
                summary: `Статус: ${prevStatus} → ${input.status}`,
                detail: { previous_status: prevStatus, new_status: input.status },
            });
        }
        return ok({ ...exp, stop_criteria: { ...exp.stop_criteria }, ...(exp.decision ? { decision: { ...exp.decision } } : {}) });
    }
    async getAbExperimentKpi(mode, experimentId) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const exp = AB_EXPERIMENTS.find((item) => item.id === experimentId && item.mode === mode);
        if (!exp)
            return fail("NOT_FOUND", "Эксперимент не найден");
        return ok(computeAbExperimentKpi({ ...exp, stop_criteria: { ...exp.stop_criteria } }));
    }
    async getAbExperimentWinnerSuggestion(mode, experimentId) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const exp = AB_EXPERIMENTS.find((item) => item.id === experimentId && item.mode === mode);
        if (!exp)
            return fail("NOT_FOUND", "Эксперимент не найден");
        const snapshot = { ...exp, stop_criteria: { ...exp.stop_criteria } };
        const kpi = computeAbExperimentKpi(snapshot);
        return ok(buildAbExperimentWinnerSuggestion(snapshot, kpi));
    }
    async commitAbExperimentOutcome(mode, experimentId, input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const exp = AB_EXPERIMENTS.find((item) => item.id === experimentId && item.mode === mode);
        if (!exp)
            return fail("NOT_FOUND", "Эксперимент не найден");
        if (exp.status === "draft") {
            return fail("VALIDATION_ERROR", "Исход можно зафиксировать только после выхода из draft (running/stopped).");
        }
        if (exp.decision) {
            return fail("VALIDATION_ERROR", "Решение по эксперименту уже зафиксировано.");
        }
        const decided_at = now();
        const snapshot = { ...exp, stop_criteria: { ...exp.stop_criteria } };
        const kpi = computeAbExperimentKpi(snapshot);
        const sug = buildAbExperimentWinnerSuggestion(snapshot, kpi);
        const fingerprint = abCriteriaFingerprint(snapshot, kpi, sug);
        const sessionUser = this.getSessionUserFromStore();
        if (input.action === "rollback") {
            exp.decision = { outcome: "rolled_back", decided_at };
            exp.status = "stopped";
        }
        else {
            const promoted_branch = input.action === "promote_a" ? "a" : "b";
            exp.decision = { outcome: "promoted", promoted_branch, decided_at };
            exp.status = "completed";
        }
        exp.updated_at = decided_at;
        pushAbDecisionLogEntry({
            id: nextAbLogId(exp.id),
            experiment_id: exp.id,
            mode: exp.mode,
            at: decided_at,
            actor: "user",
            event_type: "outcome_committed",
            summary: `Исход зафиксирован: ${input.action} (${sessionUser?.email ?? "user"})`,
            detail: {
                outcome_action: input.action,
                kpi_snapshot: cloneAbKpi(kpi),
                suggestion_snapshot: cloneAbSuggestion(sug),
                criteria_fingerprint: fingerprint,
            },
        });
        return ok({
            ...exp,
            stop_criteria: { ...exp.stop_criteria },
            ...(exp.decision ? { decision: { ...exp.decision } } : {}),
        });
    }
    async getAbExperimentDecisionLog(mode, experimentId) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const exp = AB_EXPERIMENTS.find((item) => item.id === experimentId && item.mode === mode);
        if (!exp)
            return fail("NOT_FOUND", "Эксперимент не найден");
        const bucket = AB_EXPERIMENT_DECISION_LOGS[experimentId] ?? [];
        if (bucket.length === 0) {
            const bootstrap = {
                id: `ablog-bootstrap-${experimentId}`,
                experiment_id: experimentId,
                mode,
                at: exp.created_at,
                actor: "system",
                event_type: "experiment_created",
                summary: `Журнал решений: начальная запись для «${exp.name}»`,
            };
            AB_EXPERIMENT_DECISION_LOGS[experimentId] = [bootstrap];
        }
        const rows = AB_EXPERIMENT_DECISION_LOGS[experimentId] ?? [];
        const cloneEntry = (e) => ({
            ...e,
            detail: e.detail
                ? {
                    ...e.detail,
                    kpi_snapshot: e.detail.kpi_snapshot ? cloneAbKpi(e.detail.kpi_snapshot) : undefined,
                    suggestion_snapshot: e.detail.suggestion_snapshot ? cloneAbSuggestion(e.detail.suggestion_snapshot) : undefined,
                }
                : undefined,
        });
        return ok([...rows].map(cloneEntry).reverse());
    }
    async listWhatIfExperiments(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok(WHATIF_EXPERIMENTS.filter((e) => e.mode === mode).map((e) => ({
            ...e,
            report: e.report ? { ...e.report } : null,
        })));
    }
    async createWhatIfExperiment(input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const strategy = STRATEGIES.find((s) => s.id === input.strategy_id && s.mode === input.mode);
        if (!strategy)
            return fail("NOT_FOUND", "Стратегия не найдена");
        const baseline = { ...strategy.params };
        const hypothesis = { ...baseline, ...input.params_overrides };
        const row = {
            id: `wif-${Date.now()}`,
            mode: input.mode,
            name: input.name,
            hypothesis: input.hypothesis,
            strategy_id: input.strategy_id,
            params_baseline: baseline,
            params_hypothesis: hypothesis,
            period_from: input.period_from,
            period_to: input.period_to,
            status: "draft",
            report: null,
            created_at: now(),
            updated_at: now(),
        };
        WHATIF_EXPERIMENTS.unshift(row);
        return ok({ ...row });
    }
    async updateWhatIfExperiment(id, input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const row = WHATIF_EXPERIMENTS.find((e) => e.id === id && e.mode === input.mode);
        if (!row)
            return fail("NOT_FOUND", "What-if эксперимент не найден");
        if (input.name != null)
            row.name = input.name;
        if (input.hypothesis != null)
            row.hypothesis = input.hypothesis;
        if (input.period_from != null)
            row.period_from = input.period_from;
        if (input.period_to != null)
            row.period_to = input.period_to;
        if (input.params_overrides != null) {
            const strategy = STRATEGIES.find((s) => s.id === row.strategy_id && s.mode === row.mode);
            const baseline = { ...(strategy?.params ?? row.params_baseline) };
            row.params_baseline = baseline;
            row.params_hypothesis = { ...baseline, ...input.params_overrides };
        }
        if (input.status != null) {
            if (input.status === "completed")
                return fail("VALIDATION_ERROR", "completed только через report");
            row.status = input.status;
        }
        if (input.period_from != null ||
            input.period_to != null ||
            input.params_overrides != null) {
            row.report = null;
            if (row.status === "completed")
                row.status = "draft";
        }
        row.updated_at = now();
        return ok({ ...row, report: row.report ? { ...row.report } : null });
    }
    async deleteWhatIfExperiment(mode, id) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const idx = WHATIF_EXPERIMENTS.findIndex((e) => e.id === id && e.mode === mode);
        if (idx < 0)
            return fail("NOT_FOUND", "What-if эксперимент не найден");
        WHATIF_EXPERIMENTS.splice(idx, 1);
        return ok(null);
    }
    async generateWhatIfReport(mode, experimentId) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const row = WHATIF_EXPERIMENTS.find((e) => e.id === experimentId && e.mode === mode);
        if (!row)
            return fail("NOT_FOUND", "What-if эксперимент не найден");
        row.report = {
            experiment_id: row.id,
            mode,
            strategy_id: row.strategy_id,
            disclaimer: "Mock what-if: упрощённая модель для UI.",
            fact: { pnl: 120, max_drawdown_pct: 0.05, trades_count: 4, buy_count: 2, sell_count: 2 },
            hypothesis: { pnl: 180, max_drawdown_pct: 0.04, trades_count: 6, buy_count: 3, sell_count: 3 },
            delta_pnl: 60,
            recommendation: "hypothesis_better",
            tickers: [
                {
                    ticker: "SBER",
                    fact_trades: 4,
                    hypothesis_trades: 6,
                    fact_pnl: 120,
                    hypothesis_pnl: 180,
                    bars_used: 120,
                },
            ],
            hypothesis_trades_sample: [],
            computed_at: now(),
            model_notes: ["mock"],
        };
        row.status = "completed";
        row.updated_at = now();
        return ok({ ...row, report: { ...row.report } });
    }
    /** Для тестов: повтор вычисления рекомендации по снимку KPI (должно совпадать с записью в decision-log). */
    rebuildAbWinnerSuggestionForTests(exp, kpi) {
        return buildAbExperimentWinnerSuggestion({ ...exp, stop_criteria: { ...exp.stop_criteria } }, kpi);
    }
    async listOrders(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const rows = [...ORDERS, ...SHADOW_ORDERS]
            .filter((o) => o.mode === mode)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .map((o) => withOrderLots(o));
        return ok(rows);
    }
    async getMarketInstrument(ticker, mode, orderType = "LIMIT") {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const symbol = ticker.trim().toUpperCase();
        if (!symbol)
            return fail("VALIDATION_ERROR", "Тикер обязателен");
        const schedule = buildFallbackExchangeSchedule(mode, 7);
        schedule.agent_schedule = { ...USER_TRADING_PREFS.schedule };
        const tradability = mockInstrumentTradability(symbol, orderType, schedule, USER_TRADING_PREFS.schedule);
        return ok({
            mode,
            ticker: symbol,
            lot_size: resolveLotSize(symbol),
            figi: null,
            source: "default",
            tradability,
        });
    }
    async getExchangeSchedule(mode, exchange = "MOEX") {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        void exchange;
        const schedule = buildFallbackExchangeSchedule(mode, 7);
        schedule.agent_schedule = { ...USER_TRADING_PREFS.schedule };
        return ok(schedule);
    }
    async createOrder(input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const idempotencyKey = input.client_request_id?.trim();
        if (idempotencyKey && ORDER_IDEMPOTENCY_CACHE.has(idempotencyKey)) {
            const cached = ORDER_IDEMPOTENCY_CACHE.get(idempotencyKey);
            if (cached?.success) {
                return ok({ ...cached.data });
            }
            return cached ?? fail("IDEMPOTENCY_ERROR", "Ошибка идемпотентного кеша");
        }
        const ticker = input.ticker.trim().toUpperCase();
        if (!ticker)
            return fail("VALIDATION_ERROR", "Тикер обязателен");
        if (!Number.isFinite(input.qty) || input.qty <= 0)
            return fail("VALIDATION_ERROR", "Количество должно быть положительным числом");
        if (!Number.isFinite(input.price) || input.price <= 0)
            return fail("VALIDATION_ERROR", "Цена должна быть положительным числом");
        const strategy = STRATEGIES.find((s) => s.id === input.strategy_id);
        if (!strategy)
            return fail("NOT_FOUND", "Стратегия не найдена");
        const sessionUser = this.getSessionUserFromStore();
        const normalized = { ...input, ticker };
        this.recordPortfolioOrderAttempt(normalized.mode);
        const gate = this.runPreTradeChecks(normalized);
        if (!gate.ok) {
            this.recordPortfolioRejectIfNeeded(normalized.mode, gate.code);
            this.appendRejectedTrace(normalized, gate.code, gate.message);
            this.maybeAutoDeactivateStrategy(normalized.strategy_id, normalized.mode);
            const rejected = fail(gate.code, gate.message);
            if (idempotencyKey)
                ORDER_IDEMPOTENCY_CACHE.set(idempotencyKey, rejected);
            return rejected;
        }
        const ex = EXECUTION_POLICIES[normalized.mode];
        const rt = RUNTIME_POLICIES[normalized.mode];
        const mildMd = this.isExecutionMildMarketDataDegradation(normalized.mode);
        const expectedPrice = normalized.price;
        const expectedSlippageBps = normalized.type === "MARKET"
            ? normalized.mode === "live"
                ? 10 + Math.random() * 8
                : 5 + Math.random() * 4
            : normalized.mode === "live"
                ? 4 + Math.random() * 4
                : 2 + Math.random() * 3;
        let extraAdaptiveBps = 0;
        if (mildMd && ex.adaptive_offset_enabled) {
            const pos = Math.min(1, (Date.now() - MARKET_DATA_HEALTH[normalized.mode].last_primary_at) / Math.max(1, rt.market_data.primary_stale_ms));
            extraAdaptiveBps = ex.adaptive_offset_max_extra_bps * pos * (0.45 + Math.random() * 0.55);
        }
        if (mildMd && ex.liquidity_degraded_fallback === "widen_and_slow") {
            extraAdaptiveBps += ex.adaptive_offset_max_extra_bps * 0.12;
        }
        let effectiveSlices = Math.max(1, Math.min(20, Math.floor(ex.max_child_order_slices)));
        if (mildMd && ex.liquidity_degraded_fallback === "pause_slicing") {
            effectiveSlices = 1;
        }
        else if (ex.min_child_order_qty_ratio > 0 && ex.min_child_order_qty_ratio < 1) {
            const capByMin = Math.max(1, Math.floor(1 / ex.min_child_order_qty_ratio));
            effectiveSlices = Math.max(1, Math.min(effectiveSlices, capByMin));
        }
        const passiveSlowFactor = mildMd && ex.liquidity_degraded_fallback === "widen_and_slow" && ex.routing_profile === "passive" ? 0.9 : 1;
        const realizedSlippageBps = expectedSlippageBps *
            (normalized.mode === "live" ? 0.8 + Math.random() * 0.9 : 0.7 + Math.random() * 0.7) *
            passiveSlowFactor +
            extraAdaptiveBps;
        const sideSign = normalized.side === "BUY" ? 1 : -1;
        const realizedPrice = Number((expectedPrice * (1 + (sideSign * realizedSlippageBps) / 10000)).toFixed(4));
        const partialFillRatio = normalized.type === "MARKET"
            ? Number((0.72 + 0.22 * Math.min(1, 1 / effectiveSlices) + Math.random() * 0.06).toFixed(2))
            : Number((0.5 + Math.random() * 0.5).toFixed(2));
        const baseLat = normalized.mode === "live" ? 220 + Math.random() * 380 : 90 + Math.random() * 200;
        let latencyMs = Math.round(baseLat);
        if (normalized.type === "MARKET") {
            latencyMs = Math.round(baseLat * (1 + 0.1 * (effectiveSlices - 1)));
            if (mildMd && ex.liquidity_degraded_fallback === "widen_and_slow") {
                latencyMs = Math.round(latencyMs * 1.2);
            }
        }
        const notional = this.orderNotional({ qty: normalized.qty, price: realizedPrice, ticker: normalized.ticker });
        const commissionRatePct = RISK_POLICIES[normalized.mode].commission_rate_pct ?? 0.3;
        const fee = Number((notional * (commissionRatePct / 100)).toFixed(4));
        const tax = Number((notional * 0.0002).toFixed(4));
        const executionProfile = this.resolveExecutionProfileWithAutoTwap(normalized, this.orderNotional({ qty: normalized.qty, price: normalized.price, ticker: normalized.ticker }));
        const executionVenue = this.resolveExecutionVenue(ex, mildMd);
        const isShadow = normalized.shadow_mode === true || this.shouldAutoShadowByRollout(normalized, sessionUser?.id);
        const filledLike = isShadow || normalized.type === "MARKET";
        const orderId = `o${Date.now()}`;
        const queueProxy = !filledLike && normalized.type === "LIMIT"
            ? queuePositionProxyForOrder({ id: orderId, ticker: normalized.ticker })
            : undefined;
        const lot_size = resolveLotSize(normalized.ticker);
        const order = {
            id: orderId,
            mode: normalized.mode,
            strategy_id: normalized.strategy_id,
            execution_profile: executionProfile,
            execution_venue: executionVenue,
            ticker: normalized.ticker,
            side: normalized.side,
            type: normalized.type,
            qty: normalized.qty,
            lot_size,
            shares_qty: normalized.qty * lot_size,
            price: normalized.price,
            status: isShadow ? "shadow_filled" : normalized.type === "MARKET" ? "filled" : "open",
            expected_price: expectedPrice,
            realized_price: normalized.type === "MARKET" ? realizedPrice : undefined,
            slippage_bps: normalized.type === "MARKET" ? Number(realizedSlippageBps.toFixed(2)) : undefined,
            latency_ms: normalized.type === "MARKET" ? latencyMs : undefined,
            fee: normalized.type === "MARKET" ? fee : undefined,
            tax: normalized.type === "MARKET" ? tax : undefined,
            partial_fill_ratio: normalized.type === "MARKET" ? partialFillRatio : undefined,
            created_at: now(),
            is_shadow: isShadow,
            queue_position_proxy: queueProxy,
            time_to_fill_ms: filledLike ? latencyMs : undefined,
            signal_source: normalized.signal_source,
        };
        if (isShadow) {
            SHADOW_ORDERS.unshift(order);
            SHADOW_ORDERS.splice(500);
        }
        else {
            ORDERS.unshift(order);
            ORDERS.splice(500);
        }
        const markPrice = order.realized_price ?? order.price;
        if (Number.isFinite(markPrice) && markPrice > 0) {
            LAST_MARKET_PRICE[normalized.mode] = {
                ...(LAST_MARKET_PRICE[normalized.mode] ?? {}),
                [normalized.ticker]: markPrice,
            };
        }
        if (!isShadow && order.mode === "live" && order.status === "filled" && order.realized_price != null) {
            const fillNotional = Math.abs(normalized.qty * order.realized_price);
            const netAfterFill = this.netExposureNotionalWithOrderNotional(normalized, fillNotional);
            if (netAfterFill > PORTFOLIO_LIMITS.live.net_exposure_cap_notional) {
                PORTFOLIO_KPI.live.live_net_exposure_overage_count += 1;
            }
        }
        if (!isShadow && order.status === "filled" && order.mode === "live") {
            LOSS_STREAK_BY_MODE[order.mode] += 1;
            if (LOSS_STREAK_BY_MODE[order.mode] >= RUNTIME_POLICIES[order.mode].pretrade.cooldown_loss_streak) {
                COOLDOWN_UNTIL_BY_MODE[order.mode] = Date.now() + RUNTIME_POLICIES[order.mode].pretrade.cooldown_ms;
            }
        }
        else if (!isShadow && order.status === "filled") {
            LOSS_STREAK_BY_MODE[order.mode] = 0;
            COOLDOWN_UNTIL_BY_MODE[order.mode] = 0;
        }
        this.appendAcceptedTrace(order);
        REJECT_STREAK_BY_STRATEGY[normalized.strategy_id] = 0;
        const accepted = ok({ ...order });
        if (idempotencyKey)
            ORDER_IDEMPOTENCY_CACHE.set(idempotencyKey, accepted);
        return accepted;
    }
    async cancelOrder(id) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const order = ORDERS.find((item) => item.id === id);
        if (!order)
            return fail("NOT_FOUND", "Ордер не найден");
        if (order.status !== "open")
            return fail("ORDER_NOT_OPEN", "Отменить можно только открытый ордер");
        order.status = "cancelled";
        order.cancel_replace_last = { op: "cancel", success: true };
        return ok({ ...order });
    }
    async cancelAllOrders(mode, filter) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        let cancelled = 0;
        ORDERS.forEach((order) => {
            if (order.mode !== mode)
                return;
            if (filter?.strategy_id && order.strategy_id !== filter.strategy_id)
                return;
            if (filter?.ticker && order.ticker !== filter.ticker)
                return;
            if (filter?.status && order.status !== filter.status)
                return;
            if (order.status !== "open")
                return;
            order.status = "cancelled";
            order.cancel_replace_last = { op: "cancel", success: true };
            cancelled += 1;
        });
        return ok({ cancelled });
    }
    async replaceOrder(id, input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        if (!Number.isFinite(input.price) || input.price <= 0) {
            return fail("VALIDATION_ERROR", "Цена должна быть положительным числом");
        }
        const order = ORDERS.find((item) => item.id === id);
        if (!order)
            return fail("NOT_FOUND", "Ордер не найден");
        if (order.status !== "open")
            return fail("ORDER_NOT_OPEN", "Replace доступен только для открытой заявки");
        if (order.type !== "LIMIT")
            return fail("VALIDATION_ERROR", "Replace поддерживается только для LIMIT");
        order.price = Number(input.price.toFixed(4));
        order.expected_price = order.price;
        order.cancel_replace_last = { op: "replace", success: true };
        return ok({ ...order });
    }
    async listPositions(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const locked = SELL_LOCKED_TICKERS[mode];
        const allBlocked = ALL_SELLS_BLOCKED_TICKERS[mode];
        return ok(POSITIONS.filter((item) => item.mode === mode).map((item) => ({
            ...item,
            sell_locked: locked.has(item.ticker.toUpperCase()),
            all_sells_blocked: allBlocked.has(item.ticker.toUpperCase()),
        })));
    }
    async setPositionSellLock(input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const ticker = input.ticker.trim().toUpperCase();
        if (!ticker)
            return fail("VALIDATION_ERROR", "Тикер обязателен");
        const set = SELL_LOCKED_TICKERS[input.mode];
        if (input.locked)
            set.add(ticker);
        else
            set.delete(ticker);
        return ok({
            mode: input.mode,
            ticker,
            locked: set.has(ticker),
            sell_locked_tickers: [...set].sort(),
        });
    }
    async setPositionBlockAllSells(input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const ticker = input.ticker.trim().toUpperCase();
        if (!ticker)
            return fail("VALIDATION_ERROR", "Тикер обязателен");
        const set = ALL_SELLS_BLOCKED_TICKERS[input.mode];
        if (input.blocked)
            set.add(ticker);
        else
            set.delete(ticker);
        return ok({
            mode: input.mode,
            ticker,
            blocked: set.has(ticker),
            block_all_sells_tickers: [...set].sort(),
        });
    }
    async transferPositionStrategy(input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const ticker = input.ticker.trim().toUpperCase();
        const destId = input.to_strategy_id.trim();
        if (!ticker)
            return fail("VALIDATION_ERROR", "Тикер обязателен");
        if (!destId)
            return fail("VALIDATION_ERROR", "Целевая стратегия обязательна");
        const position = POSITIONS.find((item) => item.mode === input.mode && item.ticker.toUpperCase() === ticker && Math.abs(item.qty) > 0);
        if (!position) {
            return fail("POSITION_NOT_FOUND", `Нет открытой позиции по ${ticker} для переноса.`);
        }
        if (input.from_strategy_id && input.from_strategy_id !== position.strategy_id) {
            return fail("POSITION_STRATEGY_MISMATCH", `Позиция ${ticker} принадлежит стратегии ${position.strategy_id}.`);
        }
        if (destId === position.strategy_id) {
            return fail("POSITION_TRANSFER_SAME_STRATEGY", "Целевая стратегия совпадает с текущей.");
        }
        const dest = STRATEGIES.find((item) => item.id === destId);
        if (!dest)
            return fail("NOT_FOUND", "Целевая стратегия не найдена");
        if (dest.mode !== input.mode) {
            return fail("VALIDATION_ERROR", "Целевая стратегия должна быть в том же режиме, что и позиция.");
        }
        const allowOpen = Boolean(RUNTIME_POLICIES[input.mode].pretrade.position_transfer_allow_open_orders);
        const openOrders = ORDERS.filter((order) => order.mode === input.mode &&
            order.ticker.toUpperCase() === ticker &&
            order.status === "open");
        if (openOrders.length > 0 && !allowOpen) {
            return fail("POSITION_TRANSFER_OPEN_ORDERS", `По ${ticker} есть открытые заявки (${openOrders.length}). Отмените их или разрешите перенос в настройках pre-trade.`);
        }
        const sourceId = position.strategy_id;
        const sourcePolicy = STRATEGY_POLICIES[sourceId];
        const destPolicy = STRATEGY_POLICIES[destId] ??
            mergeStrategyPolicyRow({ strategy_id: destId, mode: dest.mode, allowed_tickers: [] });
        const sourceTicker = (sourcePolicy?.tickers ?? []).find((item) => item.ticker.toUpperCase() === ticker);
        if (sourcePolicy) {
            const nextTickers = (sourcePolicy.tickers ?? []).filter((item) => item.ticker.toUpperCase() !== ticker);
            STRATEGY_POLICIES[sourceId] = mergeStrategyPolicyRow({
                ...sourcePolicy,
                tickers: nextTickers,
                allowed_tickers: nextTickers.map((item) => item.ticker),
                updated_at: now(),
            });
        }
        const destTickers = [...(destPolicy.tickers ?? [])];
        if (!destTickers.some((item) => item.ticker.toUpperCase() === ticker)) {
            destTickers.push({
                ticker,
                lock_stop_loss: sourceTicker?.lock_stop_loss !== false,
                lock_buy: Boolean(sourceTicker?.lock_buy),
            });
        }
        STRATEGY_POLICIES[destId] = mergeStrategyPolicyRow({
            ...destPolicy,
            tickers: destTickers,
            allowed_tickers: destTickers.map((item) => item.ticker),
            updated_at: now(),
        });
        position.strategy_id = destId;
        const locked = SELL_LOCKED_TICKERS[input.mode];
        const allBlocked = ALL_SELLS_BLOCKED_TICKERS[input.mode];
        return ok({
            ...position,
            sell_locked: locked.has(ticker),
            all_sells_blocked: allBlocked.has(ticker),
        });
    }
    async getDashboard(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok({
            mode,
            portfolio_value: { currency: "RUB", value: mode === "sandbox" ? 154230.5 : 120340.1 },
            pnl_daily: { currency: "RUB", value: mode === "sandbox" ? 1240.2 : -230.4 },
            active_strategies: mode === "sandbox" ? 3 : 1,
            open_orders: mode === "sandbox" ? 2 : 1,
        });
    }
    async getMarketWatchlist(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok({ mode, tickers: resolveMarketWatchlist(mode) });
    }
    async getMarketPriceHistory(mode, ticker, filter) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const symbol = ticker.trim().toUpperCase();
        const base = LAST_MARKET_PRICE[mode]?.[symbol] ??
            POSITIONS.find((p) => p.mode === mode && p.ticker === symbol)?.market_price ??
            100;
        const now = Date.now();
        const start = filter?.dateFrom ? Date.parse(filter.dateFrom) : now - 6 * 60 * 60 * 1000;
        const end = filter?.dateTo ? Date.parse(filter.dateTo) : now;
        const from = Number.isFinite(start) ? start : now - 6 * 60 * 60 * 1000;
        const to = Number.isFinite(end) ? end : now;
        const stepMs = 5 * 60 * 1000;
        const items = [];
        let prev = base * 0.985;
        for (let ts = from; ts <= to; ts += stepMs) {
            const drift = (Math.sin(ts / 600000) + Math.cos(ts / 900000)) * base * 0.004;
            const close = Math.max(0.01, Number((prev + drift * 0.15).toFixed(4)));
            const open = prev;
            const high = Math.max(open, close) * 1.001;
            const low = Math.min(open, close) * 0.999;
            items.push({
                mode,
                ticker: symbol,
                bucket_ts: new Date(ts).toISOString(),
                open,
                high,
                low,
                close,
                samples: 3,
                source: "primary",
            });
            prev = close;
        }
        const limit = filter?.limit ?? 10000;
        return ok({
            mode,
            ticker: symbol,
            retention_days: 30,
            items: items.slice(-limit),
        });
    }
    async getAnalytics(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok(summaryByMode(mode));
    }
    async getAnalyticsDetails(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok(detailsByMode(mode));
    }
    async compareAnalytics() {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok({
            sandbox: summaryByMode("sandbox"),
            live: summaryByMode("live"),
        });
    }
    async exportAnalyticsReport(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const details = detailsByMode(mode);
        const rows = ["ts,equity,drawdown"];
        details.points.forEach((point) => rows.push(`${point.ts},${point.equity},${point.drawdown}`));
        return ok({
            filename: `analytics-${mode}.csv`,
            content: rows.join("\n"),
        });
    }
    async getPnlReport(mode, filter) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const period = filter?.period ?? "all";
        const now = new Date().toISOString();
        const dayStart = new Date();
        dayStart.setUTCHours(0, 0, 0, 0);
        return ok({
            mode,
            period,
            from_ts: period === "today" ? dayStart.toISOString() : "2026-07-01T00:00:00.000Z",
            to_ts: now,
            broker_available: true,
            broker_account_id: "mock-account-1",
            broker_synced_at: now,
            totals: {
                itr_realized_pnl: -8.12,
                broker_realized_pnl: -10.5,
                diff_pnl: 2.38,
                itr_unrealized_pnl: 15.4,
                broker_unrealized_pnl: 18.2,
            },
            tickers: [
                {
                    ticker: "RAGR",
                    itr_realized_pnl: -8.12,
                    broker_realized_pnl: -10.5,
                    diff_pnl: 2.38,
                    itr_buy_count: 1,
                    itr_sell_count: 1,
                    broker_buy_count: 2,
                    broker_sell_count: 2,
                    broker_manual_buy_count: 1,
                    broker_manual_sell_count: 0,
                    itr_unrealized_pnl: 0,
                    broker_expected_yield: -2.1,
                    has_manual_activity: true,
                },
                {
                    ticker: "HYDR",
                    itr_realized_pnl: 0,
                    broker_realized_pnl: 0,
                    diff_pnl: 0,
                    itr_buy_count: 1,
                    itr_sell_count: 0,
                    broker_buy_count: 1,
                    broker_sell_count: 0,
                    broker_manual_buy_count: 0,
                    broker_manual_sell_count: 0,
                    itr_unrealized_pnl: 15.4,
                    broker_expected_yield: 20.3,
                    has_manual_activity: false,
                },
            ],
            manual_operations: [
                {
                    operation_id: "op-manual-ragr-buy",
                    order_id: null,
                    itr_order_id: null,
                    ticker: "RAGR",
                    side: "BUY",
                    date: "2026-07-21T14:20:00.000Z",
                    quantity: 10,
                    payment: -705.0,
                    commission: -1.2,
                },
            ],
        });
    }
    async getRisk(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok({ ...RISK_LIMITS[mode] });
    }
    async updateRisk(mode, input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const current = RISK_LIMITS[mode];
        RISK_LIMITS[mode] = {
            ...current,
            ...input,
            updated_at: now(),
        };
        return ok({ ...RISK_LIMITS[mode] });
    }
    async setKillSwitch(mode, enabled, scope = "mode", strategy_id) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        if (scope === "account") {
            KILL_SWITCH_STATE.account = enabled;
        }
        else if (scope === "strategy") {
            if (!strategy_id)
                return fail("VALIDATION_ERROR", "Для strategy kill switch требуется strategy_id");
            const strategy = STRATEGIES.find((item) => item.id === strategy_id);
            if (!strategy)
                return fail("NOT_FOUND", "Стратегия не найдена");
            strategy.is_active = !enabled;
            KILL_SWITCH_STATE.strategy[strategy_id] = enabled;
        }
        else {
            KILL_SWITCH_STATE.mode[mode] = enabled;
        }
        const current = RISK_LIMITS[mode];
        RISK_LIMITS[mode] = {
            ...current,
            kill_switch_enabled: enabled,
            updated_at: now(),
        };
        TRACE_ENTRIES.unshift({
            id: `tr-killswitch-${Date.now()}`,
            mode,
            correlation_id: `corr-killswitch-${Date.now()}`,
            strategy_id: strategy_id ?? "global",
            ticker: "—",
            started_at: now(),
            steps: [
                { stage: "signal", status: "ok", at: now(), detail: `Kill switch request: ${scope}` },
                {
                    stage: "risk_gate",
                    status: enabled ? "rejected" : "ok",
                    at: now(),
                    detail: enabled ? `Kill switch enabled for ${scope}` : `Kill switch disabled for ${scope}`,
                    reason_code: enabled ? `KILL_SWITCH_${scope.toUpperCase()}` : undefined,
                },
                { stage: "router", status: "pending", at: now(), detail: "Runbook: проверить активные заявки и стратегии" },
                { stage: "venue", status: "pending", at: now(), detail: "Runbook: подтвердить стабилизацию контура" },
            ],
        });
        TRACE_ENTRIES.splice(200);
        return ok({ ...RISK_LIMITS[mode] });
    }
    async getStrategyPolicy(strategyId) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const strategy = STRATEGIES.find((item) => item.id === strategyId);
        if (!strategy)
            return fail("NOT_FOUND", "Стратегия не найдена");
        if (!STRATEGY_POLICIES[strategyId]) {
            STRATEGY_POLICIES[strategyId] = mergeStrategyPolicyRow({
                strategy_id: strategyId,
                mode: strategy.mode,
                priority: 50,
                conflict_policy: "skip",
                allowed_tickers: [],
                execution_profile: "balanced",
                updated_at: now(),
            });
        }
        STRATEGY_POLICIES[strategyId] = mergeStrategyPolicyRow(STRATEGY_POLICIES[strategyId]);
        return ok({ ...STRATEGY_POLICIES[strategyId] });
    }
    async updateStrategyPolicy(strategyId, input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const current = await this.getStrategyPolicy(strategyId);
        if (!current.success)
            return current;
        const tickers = normalizeStrategyTickers(input.tickers ?? current.data.tickers, input.allowed_tickers ?? current.data.allowed_tickers);
        const mode = current.data.mode;
        for (const item of tickers) {
            const symbol = item.ticker.toUpperCase();
            const ownerPolicy = Object.entries(STRATEGY_POLICIES).find(([id, policy]) => {
                if (id === strategyId || policy.mode !== mode)
                    return false;
                return (policy.allowed_tickers ?? []).map((t) => t.toUpperCase()).includes(symbol);
            });
            if (ownerPolicy) {
                const ownerStrategy = STRATEGIES.find((s) => s.id === ownerPolicy[0]);
                return fail("TICKER_IN_OTHER_STRATEGY", `Тикер ${symbol} уже добавлен в стратегию «${ownerStrategy?.name ?? ownerPolicy[0]}» (${ownerPolicy[0]}). В одном режиме тикер может быть только в одной стратегии.`);
            }
            const openPos = POSITIONS.find((pos) => pos.mode === mode &&
                pos.ticker.toUpperCase() === symbol &&
                Math.abs(pos.qty) > 0 &&
                pos.strategy_id !== strategyId);
            if (openPos) {
                const ownerStrategy = STRATEGIES.find((s) => s.id === openPos.strategy_id);
                return fail("TICKER_OPEN_IN_OTHER_STRATEGY", `По тикеру ${symbol} уже есть открытая позиция в стратегии «${ownerStrategy?.name ?? openPos.strategy_id}» (${openPos.strategy_id}).`);
            }
        }
        const next = {
            ...current.data,
            ...input,
            tickers,
            allowed_tickers: tickers.map((item) => item.ticker),
            trading_schedule: {
                ...current.data.trading_schedule,
                ...(input.trading_schedule ?? {}),
            },
            updated_at: now(),
        };
        STRATEGY_POLICIES[strategyId] = mergeStrategyPolicyRow(next);
        return ok({ ...STRATEGY_POLICIES[strategyId] });
    }
    async getRiskPolicy(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok({ ...RISK_POLICIES[mode] });
    }
    async updateRiskPolicy(mode, input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        RISK_POLICIES[mode] = {
            ...RISK_POLICIES[mode],
            ...input,
            updated_at: now(),
        };
        return ok({ ...RISK_POLICIES[mode] });
    }
    async getExecutionPolicy(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok({ ...EXECUTION_POLICIES[mode] });
    }
    async updateExecutionPolicy(mode, input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        EXECUTION_POLICIES[mode] = {
            ...EXECUTION_POLICIES[mode],
            ...input,
            updated_at: now(),
        };
        return ok({ ...EXECUTION_POLICIES[mode] });
    }
    async getRuntimePolicy(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok({
            ...RUNTIME_POLICIES[mode],
            pretrade: { ...RUNTIME_POLICIES[mode].pretrade },
            market_data: { ...RUNTIME_POLICIES[mode].market_data },
            posttrade: { ...RUNTIME_POLICIES[mode].posttrade },
            kill_switch: { ...RUNTIME_POLICIES[mode].kill_switch, scopes: [...RUNTIME_POLICIES[mode].kill_switch.scopes] },
            shadow_rollout: {
                ...RUNTIME_POLICIES[mode].shadow_rollout,
                strategy_whitelist: [...RUNTIME_POLICIES[mode].shadow_rollout.strategy_whitelist],
                account_whitelist: [...RUNTIME_POLICIES[mode].shadow_rollout.account_whitelist],
            },
        });
    }
    async updateRuntimePolicy(mode, input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const nextScopes = input.kill_switch?.scopes ?? RUNTIME_POLICIES[mode].kill_switch.scopes;
        RUNTIME_POLICIES[mode] = {
            ...RUNTIME_POLICIES[mode],
            pretrade: { ...RUNTIME_POLICIES[mode].pretrade, ...(input.pretrade ?? {}) },
            market_data: { ...RUNTIME_POLICIES[mode].market_data, ...(input.market_data ?? {}) },
            posttrade: { ...RUNTIME_POLICIES[mode].posttrade, ...(input.posttrade ?? {}) },
            kill_switch: {
                ...RUNTIME_POLICIES[mode].kill_switch,
                ...(input.kill_switch ?? {}),
                scopes: [...nextScopes],
            },
            shadow_rollout: {
                ...RUNTIME_POLICIES[mode].shadow_rollout,
                ...(input.shadow_rollout ?? {}),
                strategy_whitelist: input.shadow_rollout?.strategy_whitelist
                    ? [...input.shadow_rollout.strategy_whitelist]
                    : [...RUNTIME_POLICIES[mode].shadow_rollout.strategy_whitelist],
                account_whitelist: input.shadow_rollout?.account_whitelist
                    ? [...input.shadow_rollout.account_whitelist]
                    : [...RUNTIME_POLICIES[mode].shadow_rollout.account_whitelist],
            },
            version: `mock-${Date.now()}`,
            effective_from: now(),
        };
        return this.getRuntimePolicy(mode);
    }
    async getPortfolioAllocatorUsage(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const limits = PORTFOLIO_LIMITS[mode];
        const strategyRows = limits.strategy_budgets.map((budget) => {
            const positionUsed = POSITIONS.filter((position) => position.mode === mode && position.strategy_id === budget.key).reduce((acc, position) => acc + Math.abs(position.qty * position.market_price), 0);
            const turnoverUsed = this.strategyNotionalUsedToday(mode, budget.key);
            return this.buildBudgetUsageRow(budget, positionUsed, turnoverUsed);
        });
        const clusterRows = limits.cluster_budgets.map((budget) => {
            const positionUsed = POSITIONS.filter((position) => {
                if (position.mode !== mode)
                    return false;
                return CLUSTER_BY_TICKER[position.ticker] === budget.key;
            }).reduce((acc, position) => acc + Math.abs(position.qty * position.market_price), 0);
            const turnoverUsed = this.clusterNotionalUsedToday(mode, budget.key);
            return this.buildBudgetUsageRow(budget, positionUsed, turnoverUsed);
        });
        const gross = POSITIONS.filter((position) => position.mode === mode).reduce((acc, position) => acc + Math.abs(position.qty * position.market_price), 0);
        const net = Math.abs(POSITIONS.filter((position) => position.mode === mode).reduce((acc, position) => acc + position.qty * position.market_price, 0));
        return ok({
            mode,
            total_position_gross: gross,
            total_position_net: net,
            total_turnover_today: this.modeNotionalUsedToday(mode),
            strategy_budgets: strategyRows,
            cluster_budgets: clusterRows,
        });
    }
    buildBudgetUsageRow(budget, positionUsed, turnoverUsed) {
        const limit = budget.limit_notional;
        const positionPct = limit > 0 ? Math.min(999, (positionUsed / limit) * 100) : 0;
        const turnoverPct = limit > 0 ? Math.min(999, (turnoverUsed / limit) * 100) : 0;
        const atLimit = limit > 0 && turnoverUsed >= limit;
        const nearLimit = limit > 0 && !atLimit && turnoverPct >= 95;
        return {
            key: budget.key,
            name: budget.name,
            limit_notional: limit,
            position_used: positionUsed,
            position_utilization_pct: Math.round(positionPct * 100) / 100,
            turnover_used_today: turnoverUsed,
            turnover_utilization_pct: Math.round(turnoverPct * 100) / 100,
            pretrade_blocks_new_orders: atLimit,
            pretrade_near_limit: nearLimit,
        };
    }
    modeNotionalUsedToday(mode) {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const from = start.getTime();
        return ORDERS.filter((order) => order.mode === mode && new Date(order.created_at).getTime() >= from && order.status !== "rejected").reduce((acc, order) => acc + this.orderNotional(order), 0);
    }
    async getPortfolioLimits(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok({
            ...PORTFOLIO_LIMITS[mode],
            strategy_budgets: PORTFOLIO_LIMITS[mode].strategy_budgets.map((item) => ({ ...item })),
            cluster_budgets: PORTFOLIO_LIMITS[mode].cluster_budgets.map((item) => ({ ...item })),
        });
    }
    async updatePortfolioLimits(mode, input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        PORTFOLIO_LIMITS[mode] = {
            ...PORTFOLIO_LIMITS[mode],
            ...input,
            strategy_budgets: (input.strategy_budgets ?? PORTFOLIO_LIMITS[mode].strategy_budgets).map((item) => ({ ...item })),
            cluster_budgets: (input.cluster_budgets ?? PORTFOLIO_LIMITS[mode].cluster_budgets).map((item) => ({ ...item })),
            updated_at: now(),
        };
        return this.getPortfolioLimits(mode);
    }
    async getPortfolioKpi(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const k = PORTFOLIO_KPI[mode];
        const max = PORTFOLIO_LIMITS[mode].max_portfolio_reject_rate;
        const rate = k.order_attempts > 0 ? k.portfolio_rejections / k.order_attempts : 0;
        return ok({
            mode,
            order_attempts: k.order_attempts,
            portfolio_rejections: k.portfolio_rejections,
            portfolio_reject_rate: rate,
            max_portfolio_reject_rate: max,
            kpi_reject_rate_ok: k.order_attempts === 0 || rate < max,
            by_reason_code: { ...k.by_code },
            live_net_exposure_overage_count: k.live_net_exposure_overage_count,
            live_net_exposure_overage_kpi_ok: k.live_net_exposure_overage_count === 0,
            updated_at: now(),
        });
    }
    buildRiskCockpit(mode) {
        const limits = RISK_LIMITS[mode];
        const pol = RISK_POLICIES[mode];
        const pos = POSITIONS.filter((p) => p.mode === mode);
        const totalUnreal = pos.reduce((s, p) => s + p.pnl, 0);
        const tradingOrders = [...ORDERS, ...SHADOW_ORDERS].filter((o) => o.mode === mode).map((o) => withOrderLots(o));
        const tradingPnl = buildTradingPnlBreakdown(tradingOrders, totalUnreal);
        const dailyLossUsed = Math.min(limits.max_daily_loss, Math.max(0, -tradingPnl.realized_pnl_today));
        const notionalUsed = mode === "live" ? 89000 : 120000;
        const tradesUsed = mode === "live" ? 10 : 22;
        const exposure = pos.map((p) => {
            const notional = Math.abs(p.qty) * p.market_price;
            return {
                ticker: p.ticker,
                notional,
                pct_of_max_exposure: pol.max_exposure_per_ticker > 0
                    ? Math.min(100, (notional / pol.max_exposure_per_ticker) * 100)
                    : 0,
            };
        });
        return {
            mode,
            currency: "RUB",
            total_unrealized_pnl: totalUnreal,
            total_realized_pnl_today: tradingPnl.realized_pnl_today,
            realized_pnl_total: tradingPnl.realized_pnl_total,
            trading_pnl_total: tradingPnl.trading_pnl_total,
            trading_pnl_today: tradingPnl.trading_pnl_today,
            daily_loss_used: dailyLossUsed,
            daily_loss_headroom: Math.max(0, limits.max_daily_loss - dailyLossUsed),
            trades_used_today: tradesUsed,
            trades_headroom: Math.max(0, limits.max_trades_per_day - tradesUsed),
            notional_used_today: notionalUsed,
            notional_headroom: Math.max(0, limits.max_notional_per_day - notionalUsed),
            exposure_by_ticker: exposure,
            policy_ref: {
                max_daily_loss: limits.max_daily_loss,
                max_notional_per_day: limits.max_notional_per_day,
                max_trades_per_day: limits.max_trades_per_day,
                max_exposure_per_ticker: pol.max_exposure_per_ticker,
            },
            updated_at: now(),
        };
    }
    async getRiskCockpit(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok(this.buildRiskCockpit(mode));
    }
    async getPortfolioComparison(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const itrPositions = POSITIONS.filter((item) => item.mode === mode).map((item) => ({
            ticker: item.ticker,
            qty: item.qty,
            avg_price: item.avg_price,
            market_price: item.market_price,
            pnl: item.pnl,
            notional: Math.abs(item.qty * item.market_price),
            strategy_ids: [item.strategy_id],
        }));
        const totalExposure = itrPositions.reduce((sum, row) => sum + row.notional, 0);
        const totalUnreal = itrPositions.reduce((sum, row) => sum + row.pnl, 0);
        return ok({
            mode,
            sync_status: "ok",
            broker: {
                synced_at: now(),
                account_id: mode === "sandbox" ? "sandbox-acc" : "live-acc",
                currency: "RUB",
                total_amount: mode === "sandbox" ? 154230.5 : 120340.1,
                available_amount: mode === "sandbox" ? 82000 : 45000,
                expected_yield: mode === "sandbox" ? 1200 : -49,
                positions: [
                    {
                        figi: "BBG004731354",
                        ticker: "ROSN",
                        instrument_type: "share",
                        qty: 10,
                        avg_price: 570,
                        current_price: 575,
                        market_value: 5750,
                        expected_yield: 50,
                        currency: "RUB",
                    },
                ],
            },
            itr: {
                positions: itrPositions,
                total_exposure: totalExposure,
                total_unrealized_pnl: totalUnreal,
            },
        });
    }
    async syncPortfolioComparison(mode) {
        return this.getPortfolioComparison(mode);
    }
    async importBrokerTrades(mode, lookbackDays = 30) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const comparison = await this.getPortfolioComparison(mode);
        if (!comparison.success)
            return comparison;
        return ok({
            import_result: {
                mode,
                lookback_days: lookbackDays,
                scanned: 3,
                imported: 1,
                enriched: 0,
                skipped_matched: 1,
                skipped_already_imported: 1,
                skipped_invalid: 0,
                order_ids: [`o-imp-mock-${mode}`],
            },
            comparison: comparison.data,
        });
    }
    async reconcileBrokerTrades(mode, lookbackDays = 90, _importOrphans = true) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const comparison = await this.getPortfolioComparison(mode);
        if (!comparison.success)
            return comparison;
        return ok({
            reconcile_result: {
                mode,
                lookback_days: lookbackDays,
                scanned: 5,
                reconciled: 2,
                unchanged: 2,
                imported: 0,
                skipped_invalid: 1,
                order_ids: [],
            },
            comparison: comparison.data,
        });
    }
    async alignBrokerPositions(mode, _refreshSnapshot = true) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const comparisonResult = await this.getPortfolioComparison(mode);
        if (!comparisonResult.success)
            return comparisonResult;
        const brokerPositions = comparisonResult.data.broker?.positions ?? [];
        for (const bp of brokerPositions) {
            if (!bp.ticker || bp.qty <= 0 || bp.avg_price == null)
                continue;
            const ticker = bp.ticker.toUpperCase();
            const existing = POSITIONS.find((p) => p.mode === mode && p.ticker === ticker);
            const market = bp.current_price ?? bp.avg_price;
            if (existing) {
                existing.qty = bp.qty;
                existing.avg_price = bp.avg_price;
                existing.market_price = market;
                existing.pnl = Number(((market - bp.avg_price) * bp.qty).toFixed(2));
            }
            else {
                POSITIONS.push({
                    id: `p-align-${mode}-${ticker}`,
                    mode,
                    strategy_id: "__broker_manual__",
                    ticker,
                    qty: bp.qty,
                    avg_price: bp.avg_price,
                    market_price: market,
                    pnl: Number(((market - bp.avg_price) * bp.qty).toFixed(2)),
                    currency: bp.currency || "RUB",
                });
            }
        }
        const comparison = await this.getPortfolioComparison(mode);
        if (!comparison.success)
            return comparison;
        return ok({
            align: {
                mode,
                synced_snapshot: true,
                updated: brokerPositions.filter((p) => p.ticker).length,
                created: 0,
                removed: 0,
                skipped: 0,
                tickers: brokerPositions.map((p) => p.ticker).filter(Boolean),
            },
            comparison: comparison.data,
        });
    }
    async getExecutionQuality(mode) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const rows = [...ORDERS, ...SHADOW_ORDERS].filter((order) => order.mode === mode);
        const filled = rows.filter((order) => order.status === "filled");
        const expectedSlippageRows = rows.filter((order) => typeof order.expected_price === "number");
        const slippageRows = filled.filter((order) => typeof order.slippage_bps === "number");
        const latencyRows = filled.filter((order) => typeof order.latency_ms === "number");
        const withCosts = filled.filter((order) => typeof order.fee === "number" && typeof order.tax === "number" && order.realized_price && order.qty);
        const avg = (values) => (values.length ? values.reduce((acc, value) => acc + value, 0) / values.length : 0);
        const avgExpectedSlippageBps = avg(expectedSlippageRows.map((order) => order.type === "MARKET" ? (mode === "live" ? 14 : 8) : mode === "live" ? 6 : 4));
        const avgRealizedSlippageBps = avg(slippageRows.map((order) => order.slippage_bps));
        const baselineRealizedSlippageBps = Math.max(0.1, avgExpectedSlippageBps * (mode === "live" ? 1.35 : 1.25));
        const targetSlippageImprovementPct = 0.1;
        const realizedSlippageImprovementPct = baselineRealizedSlippageBps > 0
            ? (baselineRealizedSlippageBps - avgRealizedSlippageBps) / baselineRealizedSlippageBps
            : 0;
        const partialFillRate = avg(filled.map((order) => (typeof order.partial_fill_ratio === "number" ? 1 - order.partial_fill_ratio : 0)));
        const avgOrderLifetimeSec = avg(latencyRows.map((order) => Math.max(1, Math.round(order.latency_ms / 12))));
        const avgLatencyMs = avg(latencyRows.map((order) => order.latency_ms));
        const avgFeeTaxRateBps = avg(withCosts.map((order) => {
            const costs = order.fee + order.tax;
            const notional = Math.abs(order.realized_price * order.qty);
            return notional > 0 ? (costs / notional) * 10000 : 0;
        }));
        const marketCount = rows.filter((order) => order.type === "MARKET").length;
        const limitCount = rows.filter((order) => order.type === "LIMIT").length;
        const totalTypes = Math.max(1, marketCount + limitCount);
        const shadowFilled = rows.filter((order) => order.is_shadow === true && typeof order.slippage_bps === "number" && order.status === "shadow_filled");
        const liveFilled = rows.filter((order) => order.is_shadow !== true && typeof order.slippage_bps === "number" && order.status === "filled");
        const shadowAvg = avg(shadowFilled.map((order) => order.slippage_bps));
        const liveAvg = avg(liveFilled.map((order) => order.slippage_bps));
        const modeSwitchDowntimeMs = 0;
        const modeSwitchNoDowntimeKpiOk = modeSwitchDowntimeMs === 0;
        const shadowLiveDivergenceMeasuredKpiOk = shadowFilled.length > 0 && liveFilled.length > 0;
        const metrics = {
            mode,
            avg_expected_slippage_bps: Number(avgExpectedSlippageBps.toFixed(2)),
            avg_realized_slippage_bps: Number(avgRealizedSlippageBps.toFixed(2)),
            avg_slippage_bps: Number(avgRealizedSlippageBps.toFixed(2)),
            baseline_realized_slippage_bps: Number(baselineRealizedSlippageBps.toFixed(2)),
            target_slippage_improvement_pct: targetSlippageImprovementPct,
            realized_slippage_improvement_pct: Number(realizedSlippageImprovementPct.toFixed(4)),
            slippage_improvement_kpi_ok: realizedSlippageImprovementPct >= targetSlippageImprovementPct,
            shadow_sample_size: shadowFilled.length,
            live_sample_size: liveFilled.length,
            shadow_avg_realized_slippage_bps: Number(shadowAvg.toFixed(2)),
            live_avg_realized_slippage_bps: Number(liveAvg.toFixed(2)),
            shadow_vs_live_slippage_gap_bps: Number((shadowAvg - liveAvg).toFixed(2)),
            mode_switch_downtime_ms: modeSwitchDowntimeMs,
            mode_switch_no_downtime_kpi_ok: modeSwitchNoDowntimeKpiOk,
            shadow_live_divergence_measured_kpi_ok: shadowLiveDivergenceMeasuredKpiOk,
            partial_fill_rate: Number(partialFillRate.toFixed(4)),
            avg_order_lifetime_sec: Number(avgOrderLifetimeSec.toFixed(1)),
            avg_latency_ms: Number(avgLatencyMs.toFixed(0)),
            avg_fee_tax_rate_bps: Number(avgFeeTaxRateBps.toFixed(2)),
            market_orders_share: Number((marketCount / totalTypes).toFixed(4)),
            limit_orders_share: Number((limitCount / totalTypes).toFixed(4)),
            sample_size: rows.length,
            updated_at: now(),
        };
        return ok(metrics);
    }
    filterTrace(mode, filter) {
        return TRACE_ENTRIES.filter((row) => {
            if (row.mode !== mode)
                return false;
            if (filter?.strategy_id && !row.strategy_id.toLowerCase().includes(filter.strategy_id.trim().toLowerCase())) {
                return false;
            }
            if (filter?.ticker && !row.ticker.toLowerCase().includes(filter.ticker.trim().toLowerCase())) {
                return false;
            }
            if (filter?.causal_tag && !row.steps.some((s) => s.stage === filter.causal_tag)) {
                return false;
            }
            const started = new Date(row.started_at).getTime();
            if (filter?.dateFrom) {
                const from = new Date(filter.dateFrom).getTime();
                if (started < from)
                    return false;
            }
            if (filter?.dateTo) {
                const to = new Date(filter.dateTo).getTime() + 24 * 60 * 60 * 1000 - 1;
                if (started > to)
                    return false;
            }
            return true;
        });
    }
    filterSignalSkips(mode, filter) {
        return SIGNAL_SKIP_ENTRIES.filter((row) => {
            if (row.mode !== mode)
                return false;
            if (filter?.strategy_id && !row.strategy_id.toLowerCase().includes(filter.strategy_id.trim().toLowerCase())) {
                return false;
            }
            if (filter?.ticker && !row.ticker.toLowerCase().includes(filter.ticker.trim().toLowerCase())) {
                return false;
            }
            if (filter?.reason_code && row.reason_code.toUpperCase() !== filter.reason_code.trim().toUpperCase()) {
                return false;
            }
            const lastAt = new Date(row.last_at).getTime();
            if (filter?.dateFrom) {
                const from = new Date(filter.dateFrom).getTime();
                if (lastAt < from)
                    return false;
            }
            if (filter?.dateTo) {
                const to = new Date(filter.dateTo).getTime() + 24 * 60 * 60 * 1000 - 1;
                if (lastAt > to)
                    return false;
            }
            return true;
        });
    }
    async listSignalSkips(mode, filter) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok(this.filterSignalSkips(mode, filter).map((row) => ({ ...row, details: { ...row.details } })));
    }
    async listTrace(mode, filter) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok(this.filterTrace(mode, filter).map((row) => ({ ...row, steps: row.steps.map((s) => ({ ...s })) })));
    }
    async exportTraceCsv(mode, filter) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        const rows = this.filterTrace(mode, filter);
        const lines = [
            "id,correlation_id,mode,strategy_id,ticker,started_at,causal_stage,status,at,detail,reason_code",
        ];
        rows.forEach((entry) => {
            entry.steps.forEach((step) => {
                lines.push(`${entry.id},${entry.correlation_id},${entry.mode},${entry.strategy_id},${entry.ticker},${entry.started_at},${step.stage},${step.status},${step.at},"${step.detail.replace(/"/g, '""')}",${step.reason_code ?? ""}`);
            });
        });
        return ok({ filename: `execution-trace-${mode}.csv`, content: lines.join("\n") });
    }
    async getTradingPreferences() {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        return ok({
            ...USER_TRADING_PREFS,
            notifications: { ...USER_TRADING_PREFS.notifications },
            schedule: { ...USER_TRADING_PREFS.schedule },
        });
    }
    async updateTradingPreferences(input) {
        await wait();
        const tr = this.tradingOrForbidden();
        if (tr)
            return tr;
        if (input.notifications) {
            USER_TRADING_PREFS = {
                ...USER_TRADING_PREFS,
                notifications: {
                    ...USER_TRADING_PREFS.notifications,
                    ...input.notifications,
                    updated_at: now(),
                },
                updated_at: now(),
            };
        }
        if (input.schedule) {
            USER_TRADING_PREFS = {
                ...USER_TRADING_PREFS,
                schedule: { ...USER_TRADING_PREFS.schedule, ...input.schedule },
                updated_at: now(),
            };
        }
        return ok({
            ...USER_TRADING_PREFS,
            notifications: { ...USER_TRADING_PREFS.notifications },
            schedule: { ...USER_TRADING_PREFS.schedule },
        });
    }
    async listUsers(params) {
        await wait();
        const ar = this.adminOrForbidden();
        if (ar)
            return ar;
        const page = Math.max(1, params?.page ?? 1);
        const page_size = Math.min(100, Math.max(5, params?.page_size ?? 10));
        const q = (params?.q ?? "").trim().toLowerCase();
        const st = params?.status ?? "all";
        let rows = USERS.map((u) => ({ ...u }));
        if (q) {
            rows = rows.filter((u) => u.email.toLowerCase().includes(q) || u.name.toLowerCase().includes(q) || u.id.toLowerCase().includes(q));
        }
        if (st !== "all") {
            rows = rows.filter((u) => u.activation_status === st);
        }
        rows.sort((a, b) => a.registered_at.localeCompare(b.registered_at));
        const total = rows.length;
        const start = (page - 1) * page_size;
        const items = rows.slice(start, start + page_size);
        return ok({ items, total, page, page_size });
    }
    async setUserActivation(userId, input) {
        await wait();
        const ar = this.adminOrForbidden();
        if (ar)
            return ar;
        const u = USERS.find((x) => x.id === userId);
        if (!u)
            return fail("NOT_FOUND", "Пользователь не найден");
        u.activation_status = input.activation_status;
        if (input.moderation_note !== undefined) {
            u.moderation_note = input.moderation_note;
        }
        return ok({ ...u });
    }
    async setUserRoles(userId, input) {
        await wait();
        const ar = this.adminOrForbidden();
        if (ar)
            return ar;
        const sessionUser = this.getSessionUserFromStore();
        const u = USERS.find((x) => x.id === userId);
        if (!u)
            return fail("NOT_FOUND", "Пользователь не найден");
        const validRoles = ["trader", "admin", "auditor"];
        const unique = new Set();
        for (const role of input.roles) {
            if (!validRoles.includes(role)) {
                return fail("VALIDATION_ERROR", `Недопустимая роль: ${role}`);
            }
            unique.add(role);
        }
        unique.add("trader");
        const nextRoles = validRoles.filter((role) => unique.has(role));
        const hadAdmin = u.roles.includes("admin");
        const willHaveAdmin = nextRoles.includes("admin");
        if (sessionUser?.id === userId && hadAdmin && !willHaveAdmin) {
            return fail("FORBIDDEN_SELF_DEMOTE", "Нельзя снять роль admin со своей учётной записи");
        }
        if (hadAdmin && !willHaveAdmin) {
            const adminCount = USERS.filter((row) => row.roles.includes("admin")).length;
            if (adminCount <= 1) {
                return fail("LAST_ADMIN", "Нельзя снять роль admin с последнего администратора");
            }
        }
        u.roles = nextRoles;
        return ok({ ...u });
    }
    async setUserSandbox(userId, input) {
        await wait();
        const ar = this.adminOrForbidden();
        if (ar)
            return ar;
        const u = USERS.find((x) => x.id === userId);
        if (!u)
            return fail("NOT_FOUND", "Пользователь не найден");
        u.sandbox_enabled = input.sandbox_enabled;
        return ok({ ...u });
    }
    async getTinvestLimits(mode = "sandbox") {
        await wait();
        const ar = this.adminOrForbidden();
        if (ar)
            return ar;
        return ok({
            overall_status: "ok",
            worst_ratio: 0.22,
            tariff_mode: mode,
            tariff_error: null,
            settings: {
                market_data_source: "candles",
                trading_status_cache_ttl_sec: 90,
            },
            mode_loads: [
                {
                    mode: "sandbox",
                    watchlist_tickers: 25,
                    market_data_used_per_min: 87,
                    market_data_limit_per_min: 600,
                    market_data_ratio: 0.145,
                    warmup_ready: true,
                    lines: [
                        {
                            id: "md_total",
                            title: "MarketData (сумма)",
                            used_per_min: 87,
                            limit_per_min: 600,
                            ratio: 0.145,
                            status: "ok",
                            detail: "mock",
                        },
                    ],
                },
            ],
            warmups: [
                {
                    mode: "sandbox",
                    ready: true,
                    enabled: true,
                    ready_at: "2026-08-05T10:00:00Z",
                    total: 25,
                    figi_cached: 25,
                    status_cached: 25,
                    figi_missing: 0,
                    status_missing: 0,
                    percent_complete: 100,
                    updated_at: "2026-08-05T10:00:00Z",
                    last_find_done: 0,
                    last_status_done: 0,
                    skipped_reason: null,
                    progress: { total: 25, figi_missing: 0, status_missing: 0, ready: true },
                },
                {
                    mode: "live",
                    ready: false,
                    enabled: true,
                    ready_at: null,
                    total: 25,
                    figi_cached: 20,
                    status_cached: 12,
                    figi_missing: 5,
                    status_missing: 13,
                    percent_complete: 48,
                    updated_at: "2026-08-05T10:05:00Z",
                    last_find_done: 3,
                    last_status_done: 8,
                    skipped_reason: null,
                    progress: { total: 25, figi_missing: 5, status_missing: 13 },
                },
            ],
            metrics: { ok: { GetLastPrices: 120 }, err: {}, rate_limit: {} },
            venue_routing: {
                by_venue: { moex: 40, spb: 5 },
                by_source: { primary: 38, sticky: 6, failover: 1 },
                failover: { moex: 1 },
            },
            hints: [],
        });
    }
    async triggerTinvestWarmup() {
        await wait();
        const ar = this.adminOrForbidden();
        if (ar)
            return ar;
        return ok({ sandbox: { ready: true, total: 25 } });
    }
}
